import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputNumber } from "primereact/inputnumber";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { InputText } from "primereact/inputtext";
import { Tag } from "primereact/tag";
import { Dialog } from "primereact/dialog";
import { TabView, TabPanel } from "primereact/tabview";
import { Menu } from "primereact/menu";
import { Checkbox } from "primereact/checkbox";
import { Toast } from "primereact/toast";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { customerService } from "../../services/CustomerService";
import ProductService from "../../services/ProductService";
import SaleService from "../../services/SaleService";
import FinancialAccountService from "../../services/FinancialAccountService";
import settingsService from "../../services/SettingsService";
import SettlementService from "../../services/SettlementService";
import SupplierService from "../../services/SupplierService";
import SaleReceipt from "../../components/Sale/SaleReceipt";
import CustomerStatementReceipt from "../../components/Customer/CustomerStatementReceipt";
import CustomerStatementReport from "../../components/Customer/CustomerStatementReport";

export default function CustomerDetailPage() {
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);
  const toast = useRef(null);
  const receiptRef = useRef(null);
  const statementReceiptRef = useRef(null);
  const statementReportRef = useRef(null);
  const printMenuRef = useRef(null);
  const activeSaleIdRef = useRef(null);
  const [activeSaleId, setActiveSaleId] = useState(null);
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [customer, setCustomer] = useState(null);
  const [fullDetail, setFullDetail] = useState(null);
  const [summary, setSummary] = useState(null);
  const [soldProducts, setSoldProducts] = useState([]);
  const [payments, setPayments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [supplierInvoices, setSupplierInvoices] = useState([]);
  const [returns, setReturns] = useState([]);
  const [upcomingPayments, setUpcomingPayments] = useState([]);
  const [activeLeftTab, setActiveLeftTab] = useState(0);
  const [viewPreference, setViewPreference] = useState(null);
  const [showPreferenceDialog, setShowPreferenceDialog] = useState(false);
  const [invoiceSearch, setInvoiceSearch] = useState("");
  const [showOnlyOpenInvoices, setShowOnlyOpenInvoices] = useState(false);
  const [showMergeDialog, setShowMergeDialog] = useState(false);
  const [selectedMergeInvoices, setSelectedMergeInvoices] = useState([]);
  const [mergeDueDate, setMergeDueDate] = useState(null);
  const [merging, setMerging] = useState(false);

  const mergedInvoices = useMemo(() => {
    const list = [];
    invoices.forEach(inv => {
      list.push({
        ...inv,
        id: inv.id,
        type: 'sales',
        invoiceNo: inv.saleNo || String(inv.id),
        date: inv.saleDate,
        totalQuantity: inv.totalQuantity,
        totalAmount: inv.totalAmount,
        paidAmount: inv.paidAmount,
        remainingAmount: inv.totalAmount - inv.paidAmount,
        dueDate: inv.dueDate,
        receivableDueItemId: inv.receivableDueItemId,
        supplierPayableDueItemId: null
      });
    });
    supplierInvoices.forEach(inv => {
      list.push({
        ...inv,
        id: inv.id,
        type: 'purchase',
        invoiceNo: inv.invoiceNo,
        date: inv.date,
        totalQuantity: inv.totalQuantity,
        totalAmount: inv.totalAmount,
        paidAmount: inv.totalAmount - inv.remainingAmount,
        remainingAmount: inv.remainingAmount,
        dueDate: inv.dueDate,
        receivableDueItemId: null,
        supplierPayableDueItemId: inv.supplierPayableDueItemId
      });
    });

    const parseDate = (dStr) => {
      if (!dStr) return new Date(0);
      const parts = dStr.split('.');
      if (parts.length === 3) {
        return new Date(parts[2], parts[1] - 1, parts[0]);
      }
      return new Date(dStr);
    };
    return list.sort((a, b) => parseDate(b.date) - parseDate(a.date));
  }, [invoices, supplierInvoices]);

  const filteredInvoices = useMemo(() => {
    if (!showOnlyOpenInvoices) return mergedInvoices;
    return mergedInvoices.filter(inv => inv.remainingAmount > 0.01);
  }, [mergedInvoices, showOnlyOpenInvoices]);


  // Payment Form States
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [selectedReceivableId, setSelectedReceivableId] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [paymentType, setPaymentType] = useState("Nakit");
  const [paymentDate, setPaymentDate] = useState(new Date());
  const [paymentDescription, setPaymentDescription] = useState("");
  const [financialAccounts, setFinancialAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [selectedSupplierId, setSelectedSupplierId] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [showProductDetails, setShowProductDetails] = useState(false);

  // Extra Payment Details
  const [checkDetails, setCheckDetails] = useState({
    bankName: "",
    bankBranch: "",
    checkNumber: "",
    dueDate: null,
    drawer: ""
  });
  const [promissoryDetails, setPromissoryDetails] = useState({
    dueDate: null,
    drawer: "",
    guarantor: ""
  });

  // Adjustment State
  const [showAdjustmentDialog, setShowAdjustmentDialog] = useState(false);
  const [adjustmentAmount, setAdjustmentAmount] = useState(0);
  const [adjustmentDescription, setAdjustmentDescription] = useState("");

  // Discount State
  const [showDiscountDialog, setShowDiscountDialog] = useState(false);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [discountDescription, setDiscountDescription] = useState("");

  // Edit Payment State
  const [showEditPaymentDialog, setShowEditPaymentDialog] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);

  const [isExpanded, setIsExpanded] = useState(false);

  // Special Prices State
  const [specialPrices, setSpecialPrices] = useState([]);
  const [showSpecialPriceDialog, setShowSpecialPriceDialog] = useState(false);
  const [specialPriceForm, setSpecialPriceForm] = useState({
    product: null,
    priceInclVat: 0,
    priceExclVat: 0,
    priceIsVatIncluded: true,
    productQuery: "",
    productsList: [],
    showProductsList: false,
    highlightIndex: 0
  });
  const [showAllSpecialPricesDialog, setShowAllSpecialPricesDialog] = useState(false);
  const [specialPricesLoading, setSpecialPricesLoading] = useState(false);
  const detailActionsMenuRef = useRef(null);

  // Statement Dialog State
  const [showStatementDialog, setShowStatementDialog] = useState(false);
  const [statementStartDate, setStatementStartDate] = useState(null);
  const [statementEndDate, setStatementEndDate] = useState(null);
  const [statementIncludePurchases, setStatementIncludePurchases] = useState(false);
  const [statementShowProducts, setStatementShowProducts] = useState(true);
  const [statementSelectedBanks, setStatementSelectedBanks] = useState([]);

  // Return Product State
  const [showReturnDialog, setShowReturnDialog] = useState(false);
  const [showReturnPaymentActionDialog, setShowReturnPaymentActionDialog] = useState(false);
  const [isInvoiceReturn, setIsInvoiceReturn] = useState(false);
  const [returnQuantity, setReturnQuantity] = useState(1);
  const [selectedReturnRow, setSelectedReturnRow] = useState(null);
  const [returnBatches, setReturnBatches] = useState([]);
  
  // Offset / Settlement States
  const [showOffsetDialog, setShowOffsetDialog] = useState(false);
  const [offsetSourceInvoice, setOffsetSourceInvoice] = useState(null);
  const [offsetTargetInvoice, setOffsetTargetInvoice] = useState(null);
  const [offsetAmount, setOffsetAmount] = useState(0);
  const [offsetMode, setOffsetMode] = useState('sales'); // 'sales' or 'purchase'
  const [showGeneralOffsetDialog, setShowGeneralOffsetDialog] = useState(false);
  const [generalOffsetAmount, setGeneralOffsetAmount] = useState(0);
  const [offsetSubmitting, setOffsetSubmitting] = useState(false);
  const [selectedOffsetCustomerInvoices, setSelectedOffsetCustomerInvoices] = useState([]);
  const [selectedOffsetSupplierInvoices, setSelectedOffsetSupplierInvoices] = useState([]);

  // Sale Detail Popup States
  const filteredAccounts = React.useMemo(() => {
    if (paymentType === "Nakit") return financialAccounts.filter(a => a.accountType === 1);
    if (paymentType === "Havale/EFT") return financialAccounts.filter(a => a.accountType === 2);
    if (paymentType === "Kredi Kartı") return financialAccounts.filter(a => a.accountType === 4);
    if (paymentType === "Sanal POS") return financialAccounts.filter(a => a.accountType === 5);
    return [];
  }, [financialAccounts, paymentType]);

  const handleSupplierChange = (supplierId) => {
    setSelectedSupplierId(supplierId);
    const matchedAcc = financialAccounts.find(
      a => a.accountType === 5 && a.supplierId === supplierId
    );
    if (matchedAcc) {
      setSelectedAccountId(matchedAcc.id);
    } else {
      setSelectedAccountId(null);
    }
  };

  const handleAccountChange = (accountId) => {
    setSelectedAccountId(accountId);
    const selectedAcc = financialAccounts.find(a => a.id === accountId);
    if (selectedAcc && selectedAcc.supplierId) {
      setSelectedSupplierId(selectedAcc.supplierId);
    } else {
      setSelectedSupplierId(null);
    }
  };

  useEffect(() => {
    if (paymentType === "Sanal POS") return;
    if (filteredAccounts.length === 1) {
      setSelectedAccountId(filteredAccounts[0].id);
    } else {
      setSelectedAccountId(null);
    }
  }, [filteredAccounts, paymentType]);

  const [saleDetailVisible, setSaleDetailVisible] = useState(false);
  const [selectedSaleDetail, setSelectedSaleDetail] = useState(null);
  const [loadingSaleDetail, setLoadingSaleDetail] = useState(false);

  const handlePrintCollection = async (transactionId) => {
    try {
      setLoadingSaleDetail(true);
      const res = await customerService.getPaymentDetail(transactionId);
      if (res.data.success) {
        receiptRef.current.printWithData(res.data.data, 'collection');
      } else {
        toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Tahsilat detayı alınamadı', life: 3000 });
      }
    } catch (err) {
      toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Yazdırma sırasında hata oluştu', life: 3000 });
    } finally {
      setLoadingSaleDetail(false);
    }
  };

  const paymentTypes = [
    { label: "Nakit", value: "Nakit" },
    { label: "Havale/EFT", value: "Havale/EFT" },
    { label: "Kredi Kartı", value: "Kredi Kartı" },
    { label: "Sanal POS", value: "Sanal POS" },
    { label: "Çek", value: "Çek" },
    { label: "Senet", value: "Senet" }
  ];

  /* =====================
     FETCH
  ===================== */
  const fetchCustomerDetail = useCallback(async () => {
    try {
      setLoading(true);
      const res = await customerService.getCustomerDetail(id);
      const data = res.data.data;

      setFullDetail(data);
      setCustomer(data.customer);
      setSummary(data.summary);
      setSoldProducts(data.soldProducts || []);
      setPayments(data.payments || []);
      setInvoices(data.invoices || []);
      setSupplierInvoices(data.supplierInvoices || []);
      setReturns(data.returns || []);
      setUpcomingPayments(data.upcomingPayments || []);
    } catch (err) {
      console.error("Detay getirilirken hata:", err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const handleAutoOffset = async (row, mode) => {
    if (!customer.relatedSupplierId) {
      toast.current.show({
        severity: "warn",
        summary: "Uyarı",
        detail: "İlişkili tedarikçi hesabı bulunmadığı için mahsuplaşma yapılamaz.",
      });
      return;
    }

    let amount = 0;
    let customerDueItemId = null;
    let supplierDueItemId = null;
    let desc = "";

    if (mode === 'sales') {
      const openSalesAmount = row.totalAmount - row.paidAmount;
      const totalOpenSupplierAmount = supplierInvoices.reduce((sum, inv) => sum + inv.remainingAmount, 0);
      amount = Math.min(openSalesAmount, totalOpenSupplierAmount);
      customerDueItemId = row.receivableDueItemId;
      desc = `${row.saleNo} nolu satış faturası için otomatik mahsuplaşma (FIFO)`;
    } else {
      const openPurchaseAmount = row.remainingAmount;
      const totalOpenCustomerAmount = invoices.reduce((sum, inv) => sum + (inv.totalAmount - inv.paidAmount), 0);
      amount = Math.min(openPurchaseAmount, totalOpenCustomerAmount);
      supplierDueItemId = row.supplierPayableDueItemId;
      desc = `${row.invoiceNo} nolu alış faturası için otomatik mahsuplaşma (FIFO)`;
    }

    if (amount <= 0.01) {
      toast.current.show({
        severity: "warn",
        summary: "Uyarı",
        detail: "Mahsup edilebilecek açık bakiye bulunamadı.",
      });
      return;
    }

    try {
      setOffsetSubmitting(true);
      const payload = {
        customerId: customer.id,
        supplierId: customer.relatedSupplierId,
        amount: amount,
        date: new Date().toISOString(),
        description: desc,
        customerDueItemId: customerDueItemId,
        supplierDueItemId: supplierDueItemId
      };
      const res = await SettlementService.createSettlement(payload);
      if (res.success !== false) {
        toast.current.show({
          severity: "success",
          summary: "Başarılı",
          detail: "Otomatik mahsuplaşma başarıyla tamamlandı.",
        });
        fetchCustomerDetail();
      } else {
        toast.current.show({
          severity: "error",
          summary: "Hata",
          detail: res.message || "İşlem başarısız.",
        });
      }
    } catch (err) {
      console.error(err);
      toast.current.show({
        severity: "error",
        summary: "Hata",
        detail: "Mahsup işlemi sırasında bir hata oluştu.",
      });
    } finally {
      setOffsetSubmitting(false);
    }
  };

  const handleOffsetSubmit = async () => {
    if (!offsetTargetInvoice || offsetAmount <= 0) {
      toast.current.show({
        severity: "warn",
        summary: "Uyarı",
        detail: "Lütfen hedef fatura ve geçerli bir tutar girin.",
      });
      return;
    }

    try {
      setOffsetSubmitting(true);
      const payload = {
        customerId: customer.id,
        supplierId: customer.relatedSupplierId,
        amount: offsetAmount,
        date: new Date().toISOString(),
        description: offsetMode === 'sales'
          ? `${offsetSourceInvoice.saleNo} nolu satış faturası ile ${offsetTargetInvoice.invoiceNo} nolu alış faturası mahsup edildi.`
          : `${offsetTargetInvoice.saleNo} nolu satış faturası ile ${offsetSourceInvoice.invoiceNo} nolu alış faturası mahsup edildi.`,
        customerDueItemId: offsetMode === 'sales' ? offsetSourceInvoice.receivableDueItemId : offsetTargetInvoice.receivableDueItemId,
        supplierDueItemId: offsetMode === 'sales' ? offsetTargetInvoice.supplierPayableDueItemId : offsetSourceInvoice.supplierPayableDueItemId
      };

      const res = await SettlementService.createSettlement(payload);
      if (res.success !== false) {
        toast.current.show({
          severity: "success",
          summary: "Başarılı",
          detail: "Mahsup işlemi başarıyla tamamlandı.",
        });
        setShowOffsetDialog(false);
        fetchCustomerDetail();
      } else {
        toast.current.show({
          severity: "error",
          summary: "Hata",
          detail: res.message || "İşlem başarısız.",
        });
      }
    } catch (err) {
      console.error(err);
      toast.current.show({
        severity: "error",
        summary: "Hata",
        detail: "Mahsup işlemi sırasında bir hata oluştu.",
      });
    } finally {
      setOffsetSubmitting(false);
    }
  };

  const calculateOffsets = useCallback((selectedCustInvoices, selectedSuppInvoices) => {
    const custItems = selectedCustInvoices.map(inv => ({
      ...inv,
      remaining: inv.totalAmount - inv.paidAmount
    }));
    const suppItems = selectedSuppInvoices.map(inv => ({
      ...inv,
      remaining: inv.remainingAmount
    }));

    const steps = [];
    let cIdx = 0;
    let sIdx = 0;

    while (cIdx < custItems.length && sIdx < suppItems.length) {
      const cust = custItems[cIdx];
      const supp = suppItems[sIdx];

      if (cust.remaining <= 0.009) { cIdx++; continue; }
      if (supp.remaining <= 0.009) { sIdx++; continue; }

      const amount = Math.min(cust.remaining, supp.remaining);
      if (amount > 0) {
        steps.push({
          customerInvoice: cust,
          supplierInvoice: supp,
          amount: amount,
          customerDueItemId: cust.receivableDueItemId,
          supplierDueItemId: supp.supplierPayableDueItemId
        });
        cust.remaining -= amount;
        supp.remaining -= amount;
      }
    }
    return steps;
  }, []);



  const offsetSteps = useMemo(() => {
    return calculateOffsets(selectedOffsetCustomerInvoices, selectedOffsetSupplierInvoices);
  }, [selectedOffsetCustomerInvoices, selectedOffsetSupplierInvoices, calculateOffsets]);

  const totalOffsetAmount = useMemo(() => {
    return offsetSteps.reduce((sum, step) => sum + step.amount, 0);
  }, [offsetSteps]);

  const handleGeneralOffsetSubmit = async () => {
    if (offsetSteps.length === 0) {
      toast.current.show({
        severity: "warn",
        summary: "Uyarı",
        detail: "Eşleştirilebilir tutar bulunamadı. Lütfen faturaları kontrol edin.",
      });
      return;
    }

    try {
      setOffsetSubmitting(true);
      
      // Perform matches sequentially
      for (const step of offsetSteps) {
        const payload = {
          customerId: customer.id,
          supplierId: customer.relatedSupplierId,
          amount: step.amount,
          date: new Date().toISOString(),
          description: `${step.customerInvoice.saleNo} nolu satış faturası ile ${step.supplierInvoice.invoiceNo} nolu alış faturası mahsup edildi.`,
          customerDueItemId: step.customerDueItemId,
          supplierDueItemId: step.supplierDueItemId
        };

        const res = await SettlementService.createSettlement(payload);
        if (res.success === false) {
          throw new Error(res.message || "Mahsup adımlarından birinde hata oluştu.");
        }
      }

      toast.current.show({
        severity: "success",
        summary: "Başarılı",
        detail: "Seçilen faturaların mahsuplaşması başarıyla tamamlandı.",
      });
      setShowGeneralOffsetDialog(false);
      fetchCustomerDetail();
    } catch (err) {
      console.error(err);
      toast.current.show({
        severity: "error",
        summary: "Hata",
        detail: err.message || "Mahsup işlemi sırasında bir hata oluştu.",
      });
    } finally {
      setOffsetSubmitting(false);
    }
  };

  const fetchCustomerSpecialPrices = useCallback(async () => {
    try {
      setSpecialPricesLoading(true);
      const res = await customerService.getCustomerSpecialPrices(id);
      const data = res.data;
      if (data && data.success !== false) {
        setSpecialPrices(data.data || []);
      }
    } catch (err) {
      console.error("Özel fiyatlar getirilirken hata:", err);
      toast.current.show({ severity: "error", summary: "Hata", detail: "Özel fiyatlar yüklenemedi." });
    } finally {
      setSpecialPricesLoading(false);
    }
  }, [id]);

  const fetchTenantSettings = useCallback(async () => {
    try {
      const res = await settingsService.getTenantSettings();
      if (res.data && res.data.success) {
        const settings = res.data.data;
        const pref = settings["CustomerDetailViewPreference"];
        if (pref) {
          setViewPreference(pref);
          if (pref === "INVOICE") {
            setActiveLeftTab(1);
          } else {
            setActiveLeftTab(0);
          }
        } else {
          setShowPreferenceDialog(true);
        }
      }
    } catch (err) {
      console.error("Görünüm ayarları alınırken hata:", err);
    }
  }, []);

  const handleSavePreference = async (choice) => {
    try {
      setSubmitting(true);
      await settingsService.saveTenantSetting({
        settingKey: "CustomerDetailViewPreference",
        settingValue: choice
      });
      setViewPreference(choice);
      if (choice === "INVOICE") {
        setActiveLeftTab(1);
      } else {
        setActiveLeftTab(0);
      }
      setShowPreferenceDialog(false);
      toast.current.show({ severity: "success", summary: "Başarılı", detail: "Görünüm tercihi kaydedildi." });
      await fetchCustomerDetail();
    } catch (err) {
      console.error("Görünüm tercihi kaydedilirken hata:", err);
      toast.current.show({ severity: "error", summary: "Hata", detail: "Görünüm tercihi kaydedilemedi." });
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (showAllSpecialPricesDialog) {
      fetchCustomerSpecialPrices();
    }
  }, [showAllSpecialPricesDialog, fetchCustomerSpecialPrices]);

  useEffect(() => {
    fetchTenantSettings();
    fetchCustomerDetail();
    FinancialAccountService.getAll().then(res => {
      if (res.success) {
        setFinancialAccounts(res.data || []);
      }
    });
    SupplierService.getAllSuppliers().then(data => {
      setSuppliers(data || []);
    });
  }, [fetchCustomerDetail, fetchTenantSettings]);

  const handleReceivePayment = async () => {
    if (paymentAmount <= 0) return;

    if (paymentType === "Sanal POS") {
      if (!selectedAccountId && !selectedSupplierId) {
        toast.current.show({ severity: "warn", summary: "Uyarı", detail: "Sanal POS işleminde hesap veya tedarikçi seçilmelidir." });
        return;
      }
    }

    try {
      setSubmitting(true);
      const payload = {
        customerId: parseInt(id),
        amount: paymentAmount,
        paymentType: paymentType,
        paymentDate: paymentDate,
        description: paymentDescription,
        financialAccountId: selectedAccountId,
        supplierId: paymentType === "Sanal POS" ? selectedSupplierId : null,
        customerReceivableDueItemId: selectedReceivableId,
        ...(paymentType === 'Çek' ? checkDetails : {}),
        ...(paymentType === 'Senet' ? promissoryDetails : {})
      };

      const res = await customerService.receivePayment(payload);

      // Reset form
      setPaymentAmount(0);
      setPaymentDescription("");
      setCheckDetails({ bankName: "", bankBranch: "", checkNumber: "", dueDate: null, drawer: "" });
      setPromissoryDetails({ dueDate: null, drawer: "", guarantor: "" });
      setSelectedReceivableId(null);
      setSelectedSupplierId(null);
      setPaymentType("Nakit");
      setShowPaymentDialog(false);

      // Refresh data
      toast.current.show({ severity: 'success', summary: 'Başarılı', detail: 'Tahsilat kaydedildi', life: 3000 });
      await fetchCustomerDetail();
      setShowPaymentDialog(false);

      // Printing Choice
      setTimeout(() => {
        confirmDialog({
          message: 'Tahsilat fişi yazdırmak ister misiniz?',
          header: 'Fiş Yazdır',
          icon: 'pi pi-print',
          acceptLabel: 'Evet, Yazdır',
          rejectLabel: 'Hayır',
          accept: async () => {
            // We need the last payment ID. It's usually in the response or we can find it in the refreshed list.
            // Since we just refreshed, let's take the first payment from the list if it matches.
            // Better yet, the backend could return the ID.
            const lastTrId = res.data?.data || res.success || res.data?.success;
            // ServiceResult usually has the value in .data or is the result itself if using a flat response.
            // Based on my Command change, it returns ServiceResult<long>
            if (typeof res.data?.data === 'number' || typeof res.data?.data === 'string') {
              handlePrintCollection(res.data.data);
            } else if (res.data && (typeof res.data === 'number' || typeof res.data === 'string')) {
              handlePrintCollection(res.data);
            }
          }
        });
      }, 500);

    } catch (err) {
      toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Ödeme alınırken hata oluştu', life: 3000 });
      console.error("Ödeme alınırken hata:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReturnProduct = (rowData, forcedDeletePayments = null, isFullInvoice = false) => {
    setIsInvoiceReturn(isFullInvoice);
    // Check if the whole invoice will become empty
    const rowProductId = rowData.productId || rowData.ProductId;
    const rowSaleId = rowData.saleId || rowData.SaleId || (isFullInvoice ? rowData.id : null);

    const sameSaleProducts = soldProducts.filter(p => (p.saleId || p.SaleId) === rowSaleId);
    const totalQtyInSale = sameSaleProducts.reduce((sum, p) => sum + p.quantity, 0);
    const willBeEmpty = (totalQtyInSale - returnQuantity) === 0;

    const proceedReturn = async (deletePayments = false, isFullInvoiceParam = isInvoiceReturn) => {
      try {
        setSubmitting(true);
        let res;
        if (isFullInvoiceParam) {
          res = await customerService.createSaleReturn({
            saleId: rowSaleId,
            customerId: parseInt(id),
            description: `Fatura iadesi: ${rowData.saleNo || rowData.id}`,
            deletePayments: deletePayments
          });
        } else {
          const batchData = returnBatches.filter(b => b.returnQty > 0).map(b => ({
            productStockBatchId: b.productStockBatchId,
            quantity: b.returnQty
          }));

          res = await customerService.createReturn({
            productId: rowProductId,
            saleId: rowSaleId,
            customerId: parseInt(id),
            quantity: returnQuantity,
            unitPrice: rowData.unitPrice,
            description: `Satıştan iade: ${rowData.saleDate}`,
            deletePayments: forcedDeletePayments !== null ? forcedDeletePayments : deletePayments,
            batches: batchData
          });
        }

        if (res.data.success || res.success) {
          setShowReturnDialog(false);
          setShowReturnPaymentActionDialog(false);
          toast.current.show({ severity: 'success', summary: 'Başarılı', detail: 'İade işlemi kaydedildi', life: 3000 });
          await fetchCustomerDetail();
        } else {
          toast.current.show({ severity: 'error', summary: 'Hata', detail: res.data?.message || res.message || 'İade alınırken hata oluştu', life: 3000 });
        }
      } catch (err) {
        toast.current.show({ severity: 'error', summary: 'Hata', detail: 'İade alınırken hata oluştu', life: 3000 });
      } finally {
        setSubmitting(false);
        setIsInvoiceReturn(false);
      }
    };

    if (forcedDeletePayments !== null) {
      proceedReturn(forcedDeletePayments, isFullInvoice);
      return;
    }

    if (willBeEmpty || isFullInvoice) {
      const sale = invoices.find(i => i.id === rowSaleId);
      const hasPayments = sale && sale.paidAmount > 0;

      if (hasPayments) {
        setShowReturnPaymentActionDialog(true);
      } else {
        confirmDialog({
          message: isFullInvoice ? 'Faturayı tamamen iade etmek istediğinize emin misiniz?' : 'Faturada ürün kalmayacak onaylıyor musunuz?',
          header: isFullInvoice ? 'Fatura İade Onayı' : 'Kritik İade Onayı',
          icon: 'pi pi-exclamation-triangle',
          acceptClassName: 'p-button-danger',
          acceptLabel: isFullInvoice ? 'Evet, Faturayı İade Et' : 'Evet, Faturayı İptal Et ve İade Al',
          rejectLabel: 'Vazgeç',
          accept: () => proceedReturn(false, isFullInvoice)
        });
      }
    } else {
      confirmDialog({
        message: `${rowData.productName} ürününden ${returnQuantity} adet iade almak istediğinize emin misiniz?`,
        header: 'İade Onayı',
        icon: 'pi pi-exclamation-triangle',
        acceptClassName: 'p-button-danger',
        acceptLabel: 'Evet, İade Al',
        rejectLabel: 'Vazgeç',
        accept: () => proceedReturn(false, false)
      });
    }
  };

  const handleApplyDiscount = async () => {
    try {
      setSubmitting(true);
      await customerService.applyDiscount({
        customerId: parseInt(id),
        amount: discountAmount,
        discountDate: new Date(),
        description: discountDescription || "Müşteri İskontosu"
      });
      setShowDiscountDialog(false);
      setDiscountAmount(0);
      setDiscountDescription("");
      toast.current.show({ severity: 'success', summary: 'Başarılı', detail: 'İskonto uygulandı', life: 3000 });
      await fetchCustomerDetail();
    } catch (err) {
      toast.current.show({ severity: 'error', summary: 'Hata', detail: 'İskonto uygulanırken hata oluştu', life: 3000 });
    } finally {
      setSubmitting(false);
    }
  };

  const handleAdjustBalance = async () => {
    try {
      setSubmitting(true);
      await customerService.adjustBalance({
        customerId: parseInt(id),
        amount: adjustmentAmount,
        adjustmentDate: new Date(),
        description: adjustmentDescription || "Manuel Bakiye Düzenleme"
      });
      setShowAdjustmentDialog(false);
      setAdjustmentAmount(0);
      setAdjustmentDescription("");
      toast.current.show({ severity: 'success', summary: 'Başarılı', detail: 'Bakiye düzenlendi', life: 3000 });
      await fetchCustomerDetail();
    } catch (err) {
      toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Bakiye düzenlenirken hata oluştu', life: 3000 });
    } finally {
      setSubmitting(false);
    }
  };

  const searchSpecialPriceProduct = async (query) => {
    if (!query || query.length < 2) {
      setSpecialPriceForm(prev => ({ ...prev, productsList: [], showProductsList: false }));
      return;
    }
    try {
      const res = await SaleService.searchProduct({ params: { query, take: 20 } });
      setSpecialPriceForm(prev => ({ ...prev, productsList: res || [], showProductsList: true, highlightIndex: 0 }));
    } catch (err) {
      setSpecialPriceForm(prev => ({ ...prev, productsList: [], showProductsList: false }));
    }
  };

  const selectSpecialPriceProduct = (product) => {
    if (!product) return;
    const vatRate = product.vatRate || 20;
    const salePrice = product.salePrice || 0;
    setSpecialPriceForm(prev => ({
      ...prev,
      product,
      productQuery: product.name || "",
      productsList: [],
      showProductsList: false,
      priceInclVat: salePrice,
      priceExclVat: Number((salePrice / (1 + vatRate / 100)).toFixed(4)),
      priceIsVatIncluded: true
    }));
  };

  const handleSaveSpecialPrice = async () => {
    if (!specialPriceForm.product) {
      toast.current.show({ severity: "warn", summary: "Uyarı", detail: "Lütfen bir ürün seçin." });
      return;
    }
    if (specialPriceForm.priceInclVat <= 0) {
      toast.current.show({ severity: "warn", summary: "Uyarı", detail: "Fiyat 0'dan büyük olmalıdır." });
      return;
    }
    try {
      setSubmitting(true);
      const payload = {
        customerId: parseInt(id),
        productId: specialPriceForm.product.id || specialPriceForm.product.Id,
        priceExclVat: specialPriceForm.priceExclVat,
        priceInclVat: specialPriceForm.priceInclVat,
        priceIsVatIncluded: specialPriceForm.priceIsVatIncluded
      };
      const res = await customerService.saveProductPrice(parseInt(id), payload);
      if (res.data && res.data.success !== false) {
        toast.current.show({ severity: "success", summary: "Başarılı", detail: "Özel fiyat başarıyla kaydedildi." });
        setShowSpecialPriceDialog(false);
        setSpecialPriceForm({
          product: null,
          priceInclVat: 0,
          priceExclVat: 0,
          priceIsVatIncluded: true,
          productQuery: "",
          productsList: [],
          showProductsList: false,
          highlightIndex: 0
        });
        fetchCustomerSpecialPrices();
      } else {
        toast.current.show({ severity: "error", summary: "Hata", detail: res.data?.message || "İşlem başarısız oldu." });
      }
    } catch (err) {
      toast.current.show({ severity: "error", summary: "Hata", detail: "Özel fiyat kaydedilirken bir hata oluştu." });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteSpecialPrice = (productId) => {
    confirmDialog({
      message: 'Bu ürüne tanımlı özel fiyatı silmek istediğinize emin misiniz?',
      header: 'Özel Fiyatı Sil',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sil',
      rejectLabel: 'Vazgeç',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        try {
          const res = await customerService.deleteProductPrice(parseInt(id), productId);
          if (res.data && res.data.success !== false) {
            toast.current.show({ severity: "success", summary: "Başarılı", detail: "Özel fiyat silindi." });
            fetchCustomerSpecialPrices();
          } else {
            toast.current.show({ severity: "error", summary: "Hata", detail: res.data?.message || "Silme işlemi başarısız oldu." });
          }
        } catch (err) {
          toast.current.show({ severity: "error", summary: "Hata", detail: "Özel fiyat silinirken bir hata oluştu." });
        }
      }
    });
  };

  const handleDeletePayment = (paymentId) => {
    confirmDialog({
      message: 'Bu işlemi silmek istediğinize emin misiniz?',
      header: 'Silme Onayı',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: 'Evet, Sil',
      rejectLabel: 'Vazgeç',
      accept: async () => {
        try {
          await customerService.deletePayment(paymentId);
          toast.current.show({ severity: 'success', summary: 'Başarılı', detail: 'İşlem silindi', life: 3000 });
          await fetchCustomerDetail();
        } catch (err) {
          toast.current.show({ severity: 'error', summary: 'Hata', detail: 'İşlem silinirken hata oluştu', life: 3000 });
        }
      }
    });
  };

  const handleShowSaleDetail = async (saleId) => {
    try {
      setLoadingSaleDetail(true);
      setSaleDetailVisible(true);
      const res = await SaleService.getSaleDetail(saleId);
      setSelectedSaleDetail(res);
    } catch (err) {
      toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Satış detayı getirilemedi', life: 3000 });
    } finally {
      setLoadingSaleDetail(false);
    }
  };

  const handleUpdatePayment = async () => {
    try {
      setSubmitting(true);
      await customerService.updatePayment({
        transactionId: editingPayment.id,
        amount: editingPayment.amount,
        paymentType: editingPayment.paymentType,
        paymentDate: new Date(editingPayment.date.split('.').reverse().join('-')), // simple parse dd.mm.yyyy
        description: editingPayment.description || "",
        financialAccountId: editingPayment.financialAccountId
      });
      setShowEditPaymentDialog(false);
      toast.current.show({ severity: 'success', summary: 'Başarılı', detail: 'İşlem güncellendi', life: 3000 });
      await fetchCustomerDetail();
    } catch (err) {
      toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Güncelleme yapılırken hata oluştu', life: 3000 });
    } finally {
      setSubmitting(false);
    }
  };

  /* =====================
     GUARDS
  ===================== */
  if (loading || (viewPreference === null && !showPreferenceDialog)) return (
    <div className="flex flex-col items-center justify-center h-[80vh] animate-fade-in">
      <i className="pi pi-spin pi-spinner text-4xl text-blue-600"></i>
      <p className="mt-4 text-slate-600 font-medium">Müşteri bilgileri yükleniyor...</p>
    </div>
  );

  if (!customer) return (
    <div className="p-6 animate-fade-in">
      <div className="bg-white shadow-sm border border-slate-200 rounded-xl p-8 text-center max-w-lg mx-auto">
        <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
          <i className="pi pi-exclamation-triangle text-red-500 text-2xl"></i>
        </div>
        <h3 className="text-xl font-bold text-slate-800 mb-2">Müşteri bulunamadı</h3>
        <p className="text-slate-500">İstediğiniz müşteri kaydı sistemde mevcut olmayabilir.</p>
      </div>
    </div>
  );
  const balance = summary.balance;

  const balanceType =
    balance < 0 ? "NEGATIVE" :
      balance > 0 ? "POSITIVE" :
        "ZERO";
  const printMenuItems = [
    {
      label: 'Yazdırma Seçenekleri',
      items: [
        {
          label: 'Bilgi Fişi',
          icon: 'pi pi-print',
          command: () => receiptRef.current.print(activeSaleIdRef.current, 'info')
        },
        {
          label: 'Tahsilat Makbuzu',
          icon: 'pi pi-money-bill',
          command: () => receiptRef.current.print(activeSaleIdRef.current, 'collection')
        },
        {
          label: 'Detaylı Bilgi Fişi',
          icon: 'pi pi-file-pdf',
          command: () => receiptRef.current.print(activeSaleIdRef.current, 'detailed')
        }
      ]
    }
  ];

  const detailActionsMenuItems = [
    {
      label: 'Detay İşlemleri',
      items: [
        {
          label: 'Bakiye Düzenle',
          icon: 'pi pi-sliders-h',
          command: () => setShowAdjustmentDialog(true)
        },
        {
          label: 'Özel Ürün Fiyat Ayarlamaları',
          icon: 'pi pi-tags',
          command: () => setShowAllSpecialPricesDialog(true)
        },
        {
          label: 'Fatura Birleştir',
          icon: 'pi pi-copy',
          command: () => handleOpenMergeDialog()
        },
        {
          label: 'İskonto Yap',
          icon: 'pi pi-percentage',
          command: () => setShowDiscountDialog(true)
        }
      ]
    }
  ];

  const handleOpenMergeDialog = () => {
    const openCredInvoices = invoices.filter(inv => inv.receivableDueItemId && inv.remainingAmount > 0.01);
    if (openCredInvoices.length < 2) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Uyarı',
        detail: 'Birleştirmek için müşteriye ait en az 2 adet açık (tahsil edilmemiş) vadeli fatura olmalıdır.',
        life: 4000
      });
      return;
    }
    setSelectedMergeInvoices(openCredInvoices.map(x => x.id));
    setMergeDueDate(null);
    setShowMergeDialog(true);
  };

  const handleToggleMergeInvoice = (saleId) => {
    if (selectedMergeInvoices.includes(saleId)) {
      setSelectedMergeInvoices(prev => prev.filter(id => id !== saleId));
    } else {
      setSelectedMergeInvoices(prev => [...prev, saleId]);
    }
  };

  const handleToggleAllMergeInvoices = (openCredInvoices) => {
    if (selectedMergeInvoices.length === openCredInvoices.length) {
      setSelectedMergeInvoices([]);
    } else {
      setSelectedMergeInvoices(openCredInvoices.map(x => x.id));
    }
  };

  const handleMergeInvoicesSubmit = async () => {
    if (selectedMergeInvoices.length < 2) {
      toast.current?.show({
        severity: 'error',
        summary: 'Hata',
        detail: 'Birleştirme işlemi için en az 2 fatura seçilmelidir.',
        life: 3000
      });
      return;
    }

    if (!mergeDueDate) {
      toast.current?.show({
        severity: 'error',
        summary: 'Hata',
        detail: 'Lütfen yeni fatura için bir vade tarihi seçiniz.',
        life: 3000
      });
      return;
    }

    try {
      setMerging(true);
      const payload = {
        customerId: parseInt(id),
        invoiceIds: selectedMergeInvoices,
        dueDate: mergeDueDate
      };
      
      const res = await customerService.mergeInvoices(payload);
      if (res.data?.success ?? true) {
        toast.current?.show({
          severity: 'success',
          summary: 'Başarılı',
          detail: 'Seçilen faturalar başarıyla birleştirildi.',
          life: 3000
        });
        setShowMergeDialog(false);
        await fetchCustomerDetail();
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Hata',
          detail: res.data?.message || 'Birleştirme işlemi sırasında hata oluştu.',
          life: 4000
        });
      }
    } catch (err) {
      console.error(err);
      toast.current?.show({
        severity: 'error',
        summary: 'Hata',
        detail: err.response?.data?.message || err.message,
        life: 4000
      });
    } finally {
      setMerging(false);
    }
  };

  /* =====================
     RENDER
  ===================== */
  const actionBodyTemplate = (rowData) => {
    return (
      <div className="flex items-center gap-2">
        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-info p-button-text hover:bg-blue-50"
          onClick={() => {
            setEditingPayment({ ...rowData });
            setShowEditPaymentDialog(true);
          }}
        />
        <Button
          icon="pi pi-trash"
          className="p-button-rounded p-button-danger p-button-text hover:bg-red-50"
          onClick={() => handleDeletePayment(rowData.id)}
        />
      </div>
    );
  };

  const totalSales = summary?.totalSales || 0;
  const totalPaid = summary?.totalPaid || 0;
  const collectedAmount = summary?.collectedAmount ?? totalPaid; // ayrı bir alan yoksa totalPaid kullan


  const collectionRate =
    totalSales > 0 ? Math.min((totalPaid / totalSales) * 100, 100) : 0;

  const isDebt = balance > 0;
  const isCredit = balance < 0;

  const statusLabel = isDebt
    ? "Borç Bakiyesi Var"
    : isCredit
      ? "Fazla Tahsilat / Alacak"
      : "Dengede";

  return (
    <div className="animate-fade-in min-h-screen  ">
      <Toast ref={toast} appendTo={mounted ? document.body : "self"} />
      <ConfirmDialog />
      <Dialog
        header="Görüntüleme Tercihi Belirleyin"
        visible={showPreferenceDialog}
        style={{ width: '450px' }}
        modal
        closable={false}
        className="p-fluid rounded-3xl overflow-hidden"
        appendTo={mounted ? document.body : "self"}
      >
        <div className="text-center p-4">
          <i className="pi pi-sliders-h text-5xl text-blue-600 mb-4 animate-bounce"></i>
          <h3 className="text-lg font-black text-slate-800 mb-2">Müşteri Detay Ekranı Tercihi</h3>
          <p className="text-xs text-slate-500 leading-relaxed mb-6">
            Müşteri detay ekranında öncelikli olarak hangi bilgileri görmek istersiniz? Bu tercihiniz sistem performansını ve sayfa açılış hızını optimize edecektir.
          </p>
          <div className="flex flex-col gap-3">
            <Button
              label="Fatura Odaklı (Önerilen - Hızlı)"
              icon="pi pi-file"
              className="p-button-raised p-button-primary py-3 rounded-xl font-bold shadow-md hover:shadow-lg transition-all"
              onClick={() => handleSavePreference("INVOICE")}
            />
            <Button
              label="Ürün Odaklı (Tüm Ürün Detayları)"
              icon="pi pi-box"
              className="p-button-outlined p-button-secondary py-3 rounded-xl font-bold hover:bg-slate-50 transition-all"
              onClick={() => handleSavePreference("PRODUCT")}
            />
          </div>
        </div>
      </Dialog>
      <style>{`
  .customer-info-expand-enter {
    animation: customerInfoExpandEnter 240ms cubic-bezier(0.22, 1, 0.36, 1);
    transform-origin: top center;
  }

  .customer-info-compact-enter {
    animation: customerInfoCompactEnter 180ms ease-out;
  }

  @keyframes customerInfoExpandEnter {
    from {
      opacity: 0;
      transform: translateY(-8px) scale(0.985);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  @keyframes customerInfoCompactEnter {
    from {
      opacity: 0;
      transform: translateY(-4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  /* Dark mode overrides for Customer Detail Page */
  .dark .bg-white {
    background-color: #1e293b !important;
    border-color: #334155 !important;
  }

  .dark .border-slate-200,
  .dark .border-slate-100 {
    border-color: #334155 !important;
  }

  .dark .bg-slate-50,
  .dark .bg-slate-100,
  .dark .bg-slate-50\/50,
  .dark .bg-slate-50\/80 {
    background-color: #0f172a !important;
    border-color: #334155 !important;
  }

  .dark .p-tabview .p-tabview-nav {
    background: #1e293b !important;
    border-color: #334155 !important;
  }
  
  .dark .p-tabview .p-tabview-nav li .p-tabview-nav-link {
    background: #1e293b !important;
    color: #94a3b8 !important;
    border-color: #334155 !important;
  }

  .dark .p-tabview .p-tabview-nav li.p-highlight .p-tabview-nav-link {
    background: #1e293b !important;
    color: #6366f1 !important;
    border-color: #6366f1 !important;
  }

  .dark .p-tabview .p-tabview-panels {
    background: #1e293b !important;
    color: #cbd5e1 !important;
    border-color: #334155 !important;
  }

  .dark .p-dialog .p-dialog-header,
  .dark .p-dialog .p-dialog-content,
  .dark .p-dialog .p-dialog-footer {
    background: #1e293b !important;
    color: #cbd5e1 !important;
    border-color: #334155 !important;
  }

  .dark .p-dialog .p-dialog-header .p-dialog-title {
    color: #ffffff !important;
  }

  .dark .p-dialog .p-dialog-header .p-dialog-header-icon {
    color: #cbd5e1 !important;
  }

  .dark .p-dialog .p-dialog-header .p-dialog-header-icon:hover {
    background: #334155 !important;
    color: #ffffff !important;
  }
  
  .dark .p-datatable .p-paginator {
    background: #1e293b !important;
    border-color: #334155 !important;
    color: #cbd5e1 !important;
  }
`}</style>
      {/* Ürün İade Dialog */}
      <Dialog
        header={
          <div className="flex items-center gap-3">
            <div className="bg-orange-100 dark:bg-orange-950/30 p-2 rounded-lg">
              <i className="pi pi-refresh text-orange-600 dark:text-orange-400 text-xl"></i>
            </div>
            <div>
              <h3 className="text-lg font-black text-slate-900 m-0">Ödeme Kaydı Bulundu</h3>
              <p className="text-xs text-slate-500 font-medium m-0">İade sonrası faturaya ait ödemeler için işlem seçin</p>
            </div>
          </div>
        }
        visible={showReturnPaymentActionDialog}
        style={{ width: '450px' }}
        onHide={() => setShowReturnPaymentActionDialog(false)}
        className="p-fluid"
        appendTo={mounted ? document.body : "self"}
        footer={(
          <div className="flex flex-col gap-2">
            <Button
              label="Ödemeleri de Sil"
              icon="pi pi-trash"
              className="p-button-danger font-black"
              onClick={() => {
                setShowReturnPaymentActionDialog(false);
                handleReturnProduct(selectedReturnRow, true, isInvoiceReturn);
              }}
            />
            <Button
              label="Ödemeleri Koru (Müşteri Alacağı Yap)"
              icon="pi pi-wallet"
              className="p-button-warning font-black"
              onClick={() => {
                setShowReturnPaymentActionDialog(false);
                handleReturnProduct(selectedReturnRow, false, isInvoiceReturn);
              }}
            />
            <Button
              label="Vazgeç / İptal"
              icon="pi pi-times"
              className="p-button-text p-button-secondary font-bold"
              onClick={() => setShowReturnPaymentActionDialog(false)}
            />
          </div>
        )}
      >
        <div className="pt-4 pb-2">
          <div className="bg-orange-50 dark:bg-orange-950/20 p-4 rounded-2xl border border-orange-100 dark:border-orange-900/30 mb-4">
            <p className="text-sm font-bold text-orange-800 dark:text-orange-300 m-0">
              Bu faturaya ait ödeme kayıtları mevcut. Fatura iptal edileceği için ödemelerin akıbetini seçmelisiniz.
            </p>
          </div>
          <ul className="text-xs text-slate-600 space-y-2 ml-4 list-disc">
            <li><b>Ödemeleri de Sil:</b> Faturaya bağlı tahsilat kayıtları ve kasa/banka hareketleri geri alınır.</li>
            <li><b>Ödemeleri Koru:</b> Tahsilat kayıtları silinmez, ödeme faturadan bağımsız hale gelerek müşterinin genel alacağına (artı bakiye) dönüşür.</li>
          </ul>
        </div>
      </Dialog>

      {/* Ürün İade Dialog */}
      <Dialog
        header={
          <div className="flex items-center gap-3">
            <div className="bg-orange-100 dark:bg-orange-950/30 p-2 rounded-lg">
              <i className="pi pi-refresh text-orange-600 dark:text-orange-400 text-xl"></i>
            </div>
            <div>
              <h3 className="text-lg font-black text-slate-900 m-0">Ürün İade Al</h3>
              <p className="text-xs text-slate-500 font-medium m-0">İade edilecek miktar bilgisini girin</p>
            </div>
          </div>
        }
        visible={showReturnDialog}
        style={{ width: '400px' }}
        onHide={() => setShowReturnDialog(false)}
        className="p-fluid"
        appendTo={mounted ? document.body : "self"}
        footer={(
          <div className="flex gap-2">
            <Button label="Vazgeç" icon="pi pi-times" className="p-button-text p-button-secondary font-bold" onClick={() => setShowReturnDialog(false)} />
            <Button label="İadeyi Onayla" icon="pi pi-check" className="!bg-orange-600 border-none font-black" onClick={() => handleReturnProduct(selectedReturnRow)} disabled={!returnQuantity || returnQuantity <= 0} />
          </div>
        )}
      >
        <div className="space-y-4 pt-4">
          {selectedReturnRow && (
            <div className="bg-slate-50 p-3 rounded-xl border border-slate-100 mb-2">
              <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">ÜRÜN</div>
              <div className="text-sm font-bold text-slate-700">{selectedReturnRow.productName}</div>
              <div className="mt-2 flex justify-between items-center">
                <span className="text-[10px] font-bold text-slate-500">Maksimum İade Edilebilir:</span>
                <span className="text-xs font-black text-slate-900">{selectedReturnRow.quantity} Adet</span>
              </div>
            </div>
          )}

          {selectedReturnRow?.batches?.length > 1 ? (
            <div className="space-y-3">
              <span className="text-[11px] font-black text-slate-500 uppercase tracking-wider block ml-1">Batch / SKT Seçimi</span>
              <div className="max-h-[300px] overflow-y-auto pr-2 space-y-2">
                {returnBatches.map((b, idx) => (
                  <div key={b.productStockBatchId} className="bg-white p-3 rounded-xl border border-slate-200 flex items-center justify-between gap-3">
                    <div className="flex flex-col flex-1">
                      <span className="text-[11px] font-black text-slate-800 uppercase tracking-tight">{b.batchNo || 'Partisiz'}</span>
                      <span className="text-[10px] text-slate-400 font-bold">{b.expirationDate ? new Date(b.expirationDate).toLocaleDateString('tr-TR') : '-'}</span>
                      <span className="text-[9px] text-blue-500 font-black mt-1">SATILAN: {b.soldQty}</span>
                    </div>
                    <div className="w-24">
                      <InputNumber
                        value={b.returnQty}
                        onValueChange={(e) => {
                          const newBatches = [...returnBatches];
                          newBatches[idx].returnQty = e.value || 0;
                          setReturnBatches(newBatches);
                          const total = newBatches.reduce((sum, item) => sum + (item.returnQty || 0), 0);
                          setReturnQuantity(total);
                        }}
                        min={0}
                        max={b.soldQty}
                        inputClassName="p-2 text-center font-black text-xs rounded-lg border-slate-200"
                      />
                    </div>
                  </div>
                ))}
              </div>
              <div className="bg-orange-50 dark:bg-orange-950/20 p-2 rounded-lg border border-orange-100 dark:border-orange-900/30 flex justify-between items-center px-4">
                <span className="text-[10px] font-black text-orange-700 dark:text-orange-400 uppercase">TOPLAM İADE</span>
                <span className="text-sm font-black text-orange-900 dark:text-orange-300">{returnQuantity} Adet</span>
              </div>
            </div>
          ) : (
            <div className="field">
              <label className="text-[11px] font-black text-slate-500 uppercase tracking-wider block mb-1.5 ml-1">İade Adedi</label>
              <InputNumber
                value={returnQuantity}
                onValueChange={(e) => setReturnQuantity(e.value)}
                min={1}
                max={selectedReturnRow?.quantity}
                placeholder="Miktar giriniz..."
                autoFocus
                className="w-full"
                inputClassName="font-black text-lg py-3 rounded-xl border-slate-200 focus:ring-2 focus:ring-orange-500"
              />
            </div>
          )}
        </div>
      </Dialog>

      {/* UNIFIED HEADER & INFO CARD */}
      <div className="bg-white shadow-xl border border-slate-200 rounded-3xl overflow-hidden no-print">
        {!isExpanded ? (
          <div className="customer-info-compact-enter p-2 md:p-3">
            <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">

              {/* Sol alan */}
              <div className="flex items-start sm:items-center gap-3 md:gap-4 min-w-0">
                <Button
                  icon="pi pi-arrow-left"
                  className="p-button-text p-button-secondary !rounded-2xl !h-11 !w-11 bg-white border border-slate-200 shadow-sm hover:bg-slate-50 shrink-0"
                  onClick={() => navigate("/customers")}
                  tooltip="Müşteri listesine dön"
                />

                <div className="relative shrink-0">
                  <div className="w-13 h-13 md:w-14 md:h-14 rounded-2xl bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/20">
                    <i className="pi pi-user text-white text-xl"></i>
                  </div>

                  <span
                    className={`absolute -right-1 -bottom-1 w-4 h-4 rounded-full border-2 border-white ${customer.isActive ? "bg-emerald-500" : "bg-red-500"
                      }`}
                  ></span>
                </div>

                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[11px] font-black text-slate-400">
                      #{customer.id}
                    </span>
                    <h1 className="text-lg md:text-xl font-black text-slate-900 m-0 leading-tight truncate max-w-[260px] md:max-w-[420px]">
                      {customer.name}
                    </h1>

                    <Tag
                      value={customer.isActive ? "Aktif" : "Pasif"}
                      severity={customer.isActive ? "success" : "danger"}
                      className="text-[9px] font-black uppercase px-2.5 py-0.5 rounded-lg"
                    />
                  </div>

                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-slate-500">
                      <i className="pi pi-phone text-slate-400"></i>
                      {customer.phone || "Telefon yok"}
                    </span>

                    <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-slate-500 max-w-[220px] truncate">
                      <i className="pi pi-envelope text-slate-400"></i>
                      {customer.email || "E-posta yok"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Orta özet alanı */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 xl:min-w-[520px]">
                <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-100 dark:bg-slate-900 px-4 py-3">
                  <div className="text-[11px] font-black text-slate-600 dark:text-slate-400 uppercase tracking-widest">
                    Toplam Satış
                  </div>
                  <div className="mt-1 text-sm font-black text-slate-800 dark:text-slate-200">
                    ₺{totalSales.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-100 dark:bg-slate-900 px-4 py-3">
                  <div className="text-[11px] font-black text-slate-600 dark:text-slate-400 uppercase tracking-widest">
                    Tahsil Edilen
                  </div>
                  <div className="mt-1 text-sm font-black text-emerald-600 dark:text-emerald-400">
                    ₺{collectedAmount.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                  </div>
                </div>

                <div
                  className={`rounded-2xl px-4 py-3 border ${isDebt
                    ? "!bg-red-50 dark:!bg-red-950/20 border-red-100 dark:border-red-900/30"
                    : isCredit
                      ? "!bg-blue-50 dark:!bg-blue-950/20 border-blue-100 dark:border-blue-900/30"
                      : "!bg-emerald-50 dark:!bg-emerald-950/20 border-emerald-100 dark:border-emerald-900/30"
                    }`}
                >
                  <div
                    className={`text-[11px] font-black uppercase tracking-widest ${isDebt
                      ? "!text-red-400"
                      : isCredit
                        ? "!text-blue-400"
                        : "!text-emerald-400"
                      }`}
                  >
                    Güncel Bakiye
                  </div>

                  <div
                    className={`mt-1 text-sm font-black ${isDebt
                      ? "text-red-600 dark:text-red-400"
                      : isCredit
                        ? "text-blue-700 dark:text-blue-400"
                        : "text-emerald-700 dark:text-emerald-400"
                      }`}
                  >
                    ₺{Math.abs(balance).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                  </div>
                </div>
              </div>

              {/* Sağ aksiyon alanı */}
              <div className="flex items-center justify-between xl:justify-end gap-2 flex-wrap">
                <Button
                  label="Tahsilat"
                  icon="pi pi-plus-circle"
                  className="p-button-sm !rounded-xl font-bold !bg-emerald-600 !border-emerald-600 shadow-md shadow-emerald-500/20"
                  onClick={() => setShowPaymentDialog(true)}
                />

                <Button
                  label="Yeni Satış"
                  icon="pi pi-shopping-cart"
                  className="p-button-sm !rounded-xl font-bold !bg-blue-600 !border-blue-600 shadow-md shadow-blue-500/20"
                  onClick={() =>
                    navigate("/sales/productsale", {
                      state: {
                        preSelectedCustomer: {
                          id: customer.id,
                          name: customer.name,
                          balance: summary.balance,
                          phone: customer.phone,
                        },
                      },
                    })
                  }
                />

                <Button
                  label="Yeni Alış"
                  icon="pi pi-download"
                  className="p-button-sm !rounded-xl font-bold !bg-orange-600 !border-orange-600 shadow-md shadow-orange-500/20"
                  onClick={() => {
                    if (customer.relatedSupplierId) {
                      navigate("/suppliers/purchase", {
                        state: {
                          supplierId: customer.relatedSupplierId,
                          supplierName: customer.relatedSupplierName,
                          fromCustomerDetail: true,
                        },
                      });
                    } else {
                      navigate("/suppliers/purchase", {
                        state: {
                          customerId: customer.id,
                          customerName: customer.name,
                          fromCustomerDetail: true,
                        },
                      });
                    }
                  }}
                />
                {customer.relatedSupplierId && (
                  <Button
                    label="Otomatik FIFO Mahsup"
                    icon="pi pi-sync"
                    className="p-button-sm !rounded-xl font-bold !bg-purple-600 !border-purple-600 shadow-md shadow-purple-500/20"
                    onClick={() => {
                      setSelectedOffsetCustomerInvoices([]);
                      setSelectedOffsetSupplierInvoices([]);
                      setGeneralOffsetAmount(0);
                      setShowGeneralOffsetDialog(true);
                    }}
                  />
                )}

                <Button
                  type="button"
                  onClick={() => setIsExpanded(true)}
                  className=" 
    group
    !h-10 !px-3
    !rounded-2xl
    !bg-white
    !border !border-slate-400
    !text-slate-700
    shadow-sm
    hover:!bg-slate-900
    hover:!border-slate-900
    hover:!text-white
    transition-all
    duration-400
  "
                >
                  <span className="flex items-center gap-2">
                    <span className="text-[11px] font-black uppercase tracking-wide">
                      Detay
                    </span>

                    <span
                      className="
        w-8 h-6 rounded-xl
        bg-slate-100
        flex items-center justify-center
        group-hover:bg-white/15
        transition-all duration-400
      "
                    >
                      <i className="pi pi-chevron-down text-[11px] group-hover:translate-y-0.5 transition-transform duration-200"></i>
                    </span>
                  </span>
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="customer-info-expand-enter p-4 md:p-5 relative">
            <button
              type="button"
              onClick={() => setIsExpanded(false)}
              className="
    absolute top-4 right-4 z-10
    group
    h-10 px-3
    rounded-2xl
    bg-white/95
    border border-slate-200
    text-slate-700
    shadow-sm
    hover:bg-slate-900
    hover:border-slate-900
    hover:text-white
    transition-all
    duration-200
  "
            >
              <span className="flex items-center gap-2">
                <span className="text-[11px] font-black uppercase tracking-wide">
                  Daralt
                </span>

                <span
                  className="
        w-6 h-6 rounded-xl
        bg-slate-100
        flex items-center justify-center
        group-hover:bg-white/15
        transition-all duration-200
      "
                >
                  <i className="pi pi-chevron-up text-[11px] group-hover:-translate-y-0.5 transition-transform duration-200"></i>
                </span>
              </span>
            </button>
            <div className="grid grid-cols-1 xl:grid-cols-12 gap-4 items-stretch">
              {/* SOL - MÜŞTERİ PROFİL KARTI */}
              <div className="xl:col-span-7 shadow-xl border border-slate-600 shadow-slate-600/50 rounded-3xl flex flex-col">
                <div className="h-full rounded-[2rem] border border-slate-200 bg-white shadow-sm overflow-hidden">
                  {/* üst bölüm */}
                  <div className="p-4 md:p-5 bg-slate-400 border-b border-slate-100 bg-gradient-to-r from-slate-100 to-darke-400 dark:from-slate-800 dark:to-slate-900/60 dark:border-slate-800">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <Button
                        icon="pi pi-arrow-left"
                        label="Geri"
                        className="p-button-text p-button-secondary !rounded-2xl h-11 px-4 bg-white border border-slate-200 shadow-sm hover:bg-slate-50"
                        onClick={() => navigate("/customers")}
                      />

                      <div className="flex items-center gap-2 flex-wrap">
                        <Tag
                          value={customer.isActive ? "Aktif" : "Pasif"}
                          severity={customer.isActive ? "success" : "danger"}
                          className="text-[10px] font-black uppercase px-3 py-1 rounded-lg"
                        />
                        <span className="text-[11px] font-bold text-slate-400">
                          ID: #{customer.id}
                        </span>
                      </div>
                    </div>

                    <div className="mt-4 flex flex-col md:flex-row md:items-center gap-4">
                      <div className="w-20 h-20 rounded-[1.75rem] bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/20 shrink-0">
                        <i className="pi pi-user text-white text-3xl"></i>
                      </div>

                      <div className="min-w-0 flex-1">
                        <h1 className="text-2xl md:text-[2rem] font-black text-slate-900 leading-tight m-0 break-words">
                          {customer.name}
                        </h1>
                        <p className="text-sm text-slate-500 font-medium mt-2">
                          Müşteri iletişim bilgileri ve hızlı işlem alanı
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* bilgi kutuları */}
                  <div className="p-4 md:p-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                      {/* Telefon */}
                      <div className="rounded-[1.25rem] border border-slate-200 dark:border-slate-800/80 bg-slate-50 dark:bg-slate-900/30 p-4 flex items-center gap-3.5 shadow-sm hover:shadow-md transition-all duration-200">
                        <div style={{ width: '40px', height: '40px' }} className="rounded-xl bg-blue-50 dark:bg-blue-950/40 flex items-center justify-center shrink-0 border border-blue-100/50 dark:border-blue-900/30">
                          <i className="pi pi-phone text-blue-600 dark:text-blue-400 text-sm"></i>
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500">
                            Telefon
                          </div>
                          <div className="mt-0.5 text-sm font-bold text-slate-800 dark:text-slate-200 truncate">
                            {customer.phone || "—"}
                          </div>
                        </div>
                      </div>

                      {/* E-Posta */}
                      <div className="rounded-[1.25rem] border border-slate-200 dark:border-slate-800/80 bg-slate-50 dark:bg-slate-900/30 p-4 flex items-center gap-3.5 shadow-sm hover:shadow-md transition-all duration-200">
                        <div style={{ width: '40px', height: '40px' }} className="rounded-xl bg-indigo-50 dark:bg-indigo-950/40 flex items-center justify-center shrink-0 border border-indigo-100/50 dark:border-indigo-900/30">
                          <i className="pi pi-envelope text-indigo-600 dark:text-indigo-400 text-sm"></i>
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500">
                            E-Posta
                          </div>
                          <div className="mt-0.5 text-sm font-bold text-slate-800 dark:text-slate-200 break-all truncate">
                            {customer.email || "—"}
                          </div>
                        </div>
                      </div>

                      {/* Açılış Bakiyesi */}
                      <div className="rounded-[1.25rem] border border-slate-200 dark:border-slate-800/80 bg-slate-50 dark:bg-slate-900/30 p-4 flex items-center gap-3.5 shadow-sm hover:shadow-md transition-all duration-200">
                        <div style={{ width: '40px', height: '40px' }} className="rounded-xl bg-emerald-50 dark:bg-emerald-950/40 flex items-center justify-center shrink-0 border border-emerald-100/50 dark:border-emerald-900/30">
                          <i className="pi pi-money-bill text-emerald-600 dark:text-emerald-400 text-sm"></i>
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500">
                            Açılış Bakiyesi
                          </div>
                          <div className="mt-0.5 text-sm font-bold text-slate-800 dark:text-slate-200">
                            ₺{(customer.openingBalance || 0).toLocaleString("tr-TR", {
                              minimumFractionDigits: 2,
                            })}
                          </div>
                        </div>
                      </div>

                      {/* Adres */}
                      <div className="rounded-[1.25rem] border border-slate-200 dark:border-slate-800/80 bg-slate-50 dark:bg-slate-900/30 p-4 flex items-center gap-3.5 shadow-sm hover:shadow-md transition-all duration-200">
                        <div style={{ width: '40px', height: '40px' }} className="rounded-xl bg-amber-50 dark:bg-amber-950/40 flex items-center justify-center shrink-0 border border-amber-100/50 dark:border-amber-900/30">
                          <i className="pi pi-map-marker text-amber-600 dark:text-amber-400 text-sm"></i>
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500">
                            Adres
                          </div>
                          <div className="mt-0.5 text-sm font-bold text-slate-800 dark:text-slate-200 truncate" title={customer.address}>
                            {customer.address || "Adres bilgisi tanımlanmamış"}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* aksiyon alanı */}
                  <div className="px-4 md:px-5 pb-4 md:pb-5 mt-auto">
                    <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50/80 p-3">
                      <div className="grid grid-cols-3 gap-3">
                        <Button
                          label="Tahsilat Al"
                          icon="pi pi-plus-circle"
                          className="!rounded-2xl !h-12 text-sm font-bold !bg-emerald-600 !border-emerald-600 hover:!bg-emerald-700 shadow-md shadow-emerald-500/20"
                          onClick={() => setShowPaymentDialog(true)}
                        />

                        <Button
                          label="Yeni Satış"
                          icon="pi pi-shopping-cart"
                          className="!rounded-2xl !h-12 text-sm font-bold !bg-blue-600 !border-blue-600 hover:!bg-blue-700 shadow-md shadow-blue-500/20"
                          onClick={() =>
                            navigate("/sales/productsale", {
                              state: {
                                preSelectedCustomer: {
                                  id: customer.id,
                                  name: customer.name,
                                  balance: summary.balance,
                                  phone: customer.phone,
                                },
                              },
                            })
                          }
                        />

                        <Button
                          label="Düzenle"
                          icon="pi pi-user-edit"
                          className="p-button-outlined !rounded-2xl !h-12 text-white text-sm font-bold bg-amber-500 border-amber-300 text-slate-700 hover:bg-amber-600"
                          onClick={() => navigate(`/customers/edit/${id}`)}
                        />

                        <Button
                          label="Detay İşlemler"
                          icon="pi pi-cog"
                          className="p-button-outlined !rounded-2xl !h-12 text-sm font-bold border-slate-300 text-slate-700 hover:bg-slate-300"
                          onClick={(e) => detailActionsMenuRef.current.toggle(e)}
                        />

                        <Button
                          label="Ekstre"
                          icon="pi pi-file-pdf"
                          className="!rounded-2xl !h-12 text-sm font-bold !bg-slate-700 !border-slate-700 hover:!bg-slate-800 shadow-md"
                          onClick={() => setShowStatementDialog(true)}
                        />

                        {customer.relatedSupplierId && (
                          <Button
                            label="Otomatik FIFO Mahsup"
                            icon="pi pi-sync"
                            className="!rounded-2xl !h-12 text-sm font-bold !bg-purple-600 !border-purple-600 hover:!bg-purple-700 shadow-md shadow-purple-500/20 text-white"
                            onClick={() => {
                              setSelectedOffsetCustomerInvoices([]);
                              setSelectedOffsetSupplierInvoices([]);
                              setGeneralOffsetAmount(0);
                              setShowGeneralOffsetDialog(true);
                            }}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* SAĞ - NET DURUM KARTI */}
              <div className="xl:col-span-5">
                <div
                  className={`h-full rounded-[2rem] p-5 md:p-6 border relative overflow-hidden shadow-sm ${isDebt
                    ? "bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 border-slate-800 text-white dark:from-slate-950 dark:via-slate-950 dark:to-slate-900 dark:border-slate-800"
                    : "bg-gradient-to-br from-white via-white to-slate-50 border-slate-200 text-slate-900 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800/80 dark:border-slate-800 dark:text-white"
                    }`}
                >
                  <div
                    className={`absolute -top-10 -right-10 w-40 h-40 rounded-full blur-3xl ${isDebt ? "bg-emerald-500/10" : "bg-emerald-100/70 dark:bg-emerald-950/20"
                      }`}
                  ></div>

                  <div className="relative z-10 h-full flex flex-col justify-between gap-5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div
                          className={`w-2 h-12 rounded-2xl flex items-center justify-center ${isDebt ? "bg-white/10" : "bg-slate-100 dark:bg-slate-950/40"
                            }`}
                        >
                          <i
                            className={`pi pi-wallet text-lg ${isDebt ? "text-white" : "text-slate-700 dark:text-slate-300"
                              }`}
                          ></i>
                        </div>

                        <div className="flex-1 min-w-0">
                          <div
                            className={`text-[11px] font-black uppercase tracking-[0.16em] ${isDebt ? "text-white/60" : "text-slate-500"
                              }`}
                          >
                            Net Durum
                          </div>
                          <div
                            className={`text-sm font-semibold mt-1 whitespace-nowrap ${isDebt ? "text-white/75" : "text-slate-600"
                              }`}
                          >
                            {statusLabel}
                          </div>
                        </div>
                      </div>

                      <span
                        className={`px-6 py-4 rounded-full text-[12px] font-black uppercase tracking-[0.12em] ${isDebt
                          ? "bg-amber-400/15 text-amber-300"
                          : isCredit
                            ? "bg-blue-50 text-blue-700 border border-blue-100 dark:bg-blue-950/30 dark:text-blue-300 dark:border-blue-800/40"
                            : "bg-emerald-50 text-emerald-700 border border-emerald-100 dark:bg-emerald-950/30 dark:text-emerald-300 dark:border-emerald-800/40"
                          }`}
                      >
                        {isDebt ? "Borç" : isCredit ? "Alacak" : "Dengede"}
                      </span>
                    </div>

                    <div>
                      <div
                        className={`text-[14px] font-semibold mb-2 ${isDebt ? "text-white/55" : "text-slate-500"
                          }`}
                      >
                        Güncel Bakiye
                      </div>

                      <div
                        className={`text-3xl md:text-[2.6rem] leading-none font-black tracking-tight ${isDebt ? "text-white" : "text-slate-900"
                          }`}
                      >
                        ₺{Math.abs(balance).toLocaleString("tr-TR", {
                          minimumFractionDigits: 2,
                        })}
                      </div>

                      <p
                        className={`mt-3 text-[13px] ${isDebt ? "text-white/55" : "text-slate-500"
                          }`}
                      >
                        {isDebt
                          ? "Tahsil edilmesi gereken güncel müşteri bakiyesi."
                          : isCredit
                            ? "Müşteri tarafında fazla tahsilat / alacak durumu bulunuyor."
                            : "Müşterinin hesabı şu anda dengede görünüyor."}
                      </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                      {/* Son Yapılan İskonto */}
                      <div
                        className={`rounded-[1.25rem] px-4 py-4 border shadow-sm flex flex-col justify-between transition-all duration-200 ${isDebt
                          ? "bg-white/5 border-white/10"
                          : "bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-800"
                          }`}
                      >
                        <div className="flex items-center justify-between">
                          <span
                            className={`text-[10px] font-black uppercase tracking-[0.12em] ${isDebt ? "text-white/45" : "text-slate-400 dark:text-slate-500"
                              }`}
                          >
                            Son Yapılan İskonto
                          </span>
                          <div style={{ width: '24px', height: '24px' }} className={`rounded-lg flex items-center justify-center shrink-0 ${isDebt ? 'bg-white/10 text-white/70' : 'bg-slate-200/50 text-slate-500 dark:bg-slate-800 dark:text-slate-400'}`}>
                            <i className="pi pi-percentage text-[10px]"></i>
                          </div>
                        </div>
                        <div
                          className={`mt-2 text-base font-black ${isDebt ? "text-white" : "text-slate-900 dark:text-slate-200"
                            }`}
                        >
                          ₺{(summary?.lastDiscount || 0).toLocaleString("tr-TR", {
                            minimumFractionDigits: 2,
                          })}
                        </div>
                      </div>

                      {/* Toplam Yapılan İskonto */}
                      <div
                        className={`rounded-[1.25rem] px-4 py-4 border shadow-sm flex flex-col justify-between transition-all duration-200 ${isDebt
                          ? "bg-white/5 border-white/10"
                          : "bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-800"
                          }`}
                      >
                        <div className="flex items-center justify-between">
                          <span
                            className={`text-[10px] font-black uppercase tracking-[0.12em] ${isDebt ? "text-white/45" : "text-slate-400 dark:text-slate-500"
                              }`}
                          >
                            Toplam Yapılan İskonto
                          </span>
                          <div style={{ width: '24px', height: '24px' }} className={`rounded-lg flex items-center justify-center shrink-0 ${isDebt ? 'bg-white/10 text-white/70' : 'bg-slate-200/50 text-slate-500 dark:bg-slate-800 dark:text-slate-400'}`}>
                            <i className="pi pi-percentage text-[10px]"></i>
                          </div>
                        </div>
                        <div
                          className={`mt-2 text-base font-black ${isDebt ? "text-white" : "text-slate-900 dark:text-slate-200"
                            }`}
                        >
                          ₺{(summary?.totalDiscount || 0).toLocaleString("tr-TR", {
                            minimumFractionDigits: 2,
                          })}
                        </div>
                      </div>

                      {/* Tahsil Edilen */}
                      <div
                        className={`rounded-[1.25rem] px-4 py-4 border shadow-sm flex flex-col justify-between transition-all duration-200 ${isDebt
                          ? "bg-white/5 border-white/10"
                          : "bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-800"
                          }`}
                      >
                        <div className="flex items-center justify-between">
                          <span
                            className={`text-[10px] font-black uppercase tracking-[0.12em] ${isDebt ? "text-white/45" : "text-slate-400 dark:text-slate-500"
                              }`}
                          >
                            Tahsil Edilen
                          </span>
                          <div style={{ width: '24px', height: '24px' }} className={`rounded-lg flex items-center justify-center shrink-0 ${isDebt ? 'bg-white/10 text-white/70' : 'bg-slate-200/50 text-slate-500 dark:bg-slate-800 dark:text-slate-400'}`}>
                            <i className="pi pi-check-circle text-[10px]"></i>
                          </div>
                        </div>
                        <div
                          className={`mt-2 text-base font-black ${isDebt ? "text-white" : "text-slate-900 dark:text-slate-200"
                            }`}
                        >
                          ₺{collectedAmount.toLocaleString("tr-TR", {
                            minimumFractionDigits: 2,
                          })}
                        </div>
                      </div>

                      {/* Toplam Satış */}
                      <div
                        className={`rounded-[1.25rem] px-4 py-4 border shadow-sm flex flex-col justify-between transition-all duration-200 ${isDebt
                          ? "bg-white/5 border-white/10"
                          : "bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-800"
                          }`}
                      >
                        <div className="flex items-center justify-between">
                          <span
                            className={`text-[10px] font-black uppercase tracking-[0.12em] ${isDebt ? "text-white/45" : "text-slate-400 dark:text-slate-500"
                              }`}
                          >
                            Toplam Satış
                          </span>
                          <div style={{ width: '24px', height: '24px' }} className={`rounded-lg flex items-center justify-center shrink-0 ${isDebt ? 'bg-white/10 text-white/70' : 'bg-slate-200/50 text-slate-500 dark:bg-slate-800 dark:text-slate-400'}`}>
                            <i className="pi pi-shopping-bag text-[10px]"></i>
                          </div>
                        </div>
                        <div
                          className={`mt-2 text-base font-black ${isDebt ? "text-white" : "text-slate-900 dark:text-slate-200"
                            }`}
                        >
                          ₺{totalSales.toLocaleString("tr-TR", {
                            minimumFractionDigits: 2,
                          })}
                        </div>
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span
                          className={`text-[11px] font-bold ${isDebt ? "text-white/55" : "text-slate-500"
                            }`}
                        >
                          Tahsilat İlerlemesi
                        </span>
                        <span
                          className={`text-[11px] font-black ${isDebt ? "text-white/80" : "text-slate-700"
                            }`}
                        >
                          %{collectionRate.toFixed(0)}
                        </span>
                      </div>

                      <div
                        className={`h-2.5 rounded-full overflow-hidden ${isDebt ? "bg-white/10" : "bg-slate-100"
                          }`}
                      >
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-emerald-500 transition-all duration-500"
                          style={{ width: `${collectionRate}%` }}
                        ></div>
                      </div>
                    </div>

                  </div>
                </div>
              </div>

            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-6 no-print">
        {/* Sol Taraf: Alınan Ürünler ve Faturalar */}
        <div className="lg:col-span-8 bg-white shadow-xl border border-slate-200 rounded-3xl overflow-hidden">
          <TabView className="customer-detail-tabs" activeIndex={activeLeftTab} onTabChange={(e) => setActiveLeftTab(e.index)}>
            {viewPreference !== "INVOICE" && (
              <TabPanel header="Alınan Ürünler" leftIcon="pi pi-box mr-2">
                <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="bg-blue-600 w-1.5 h-5 rounded-full"></div>
                    <h2 className="text-xs font-black text-slate-700 uppercase tracking-widest m-0">Alınan Ürünler</h2>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        inputId="showDetails"
                        onChange={e => setShowProductDetails(e.checked)}
                        checked={showProductDetails}
                        className="p-checkbox-sm"
                      />
                      <label htmlFor="showDetails" className="text-[11px] font-bold text-slate-600 cursor-pointer">Detayları Göster</label>
                    </div>
                    <Tag value={`${soldProducts.length} KALEM`} className="!bg-slate-900 text-[10px] font-black" />
                  </div>
                </div>
                <DataTable
                  value={soldProducts}
                  rows={25}
                  paginator
                  responsiveLayout="scroll"
                  emptyMessage="Henüz ürün satışı yapılmamış."
                  className="p-datatable-sm"
                  paginatorClassName="border-t border-slate-100 px-4 py-2 bg-slate-50"
                  rowClassName={() => 'hover:bg-blue-50/20 transition-colors'}
                >
                  <Column field="saleDate" header="TARİH" sortable className="text-slate-500 text-[11px] font-bold" />
                  <Column field="productName" header="ÜRÜN ADI" sortable className="text-slate-800 font-black text-xs" />
                  <Column field="quantity" header="ADET" sortable body={(rowData) => <span className="px-2 py-0.5 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-md font-black text-[10px] border border-slate-200 dark:border-slate-700">{rowData.quantity}</span>} />
                  {showProductDetails && (
                    <Column field="unitPrice" header="B. FİYAT" body={(rowData) => <span className="text-slate-600 text-xs font-bold">₺{rowData.unitPrice.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</span>} />
                  )}
                  {showProductDetails && (
                    <Column field="totalAmount" header="TOPLAM" body={(rowData) => <span className="text-slate-900 text-xs font-black">₺{rowData.totalAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</span>} />
                  )}
                  <Column field="description" header="AÇIKLAMA" className="text-slate-500 text-[11px]" />
                  <Column
                    header="İADE"
                    body={(rowData) => (
                      <Button
                        icon="pi pi-refresh"
                        className="p-button-rounded p-button-warning p-button-text hover:bg-orange-50"
                        onClick={() => {
                          setSelectedReturnRow(rowData);
                          if (rowData.batches && rowData.batches.length > 0) {
                            setReturnBatches(rowData.batches.map(b => ({
                              productStockBatchId: b.productStockBatchId,
                              batchNo: b.batchNo,
                              expirationDate: b.expirationDate,
                              soldQty: b.quantity,
                              returnQty: 0
                            })));
                            setReturnQuantity(0);
                          } else {
                            setReturnBatches([]);
                            setReturnQuantity(1);
                          }
                          setShowReturnDialog(true);
                        }}
                        tooltip="İade Al"
                        tooltipOptions={{ position: 'top' }}
                      />
                    )}
                    className="w-16"
                  />
                </DataTable>
              </TabPanel>
            )}
            <TabPanel header="Faturalar" leftIcon="pi pi-file mr-2">
              <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="bg-indigo-600 w-1.5 h-5 rounded-full"></div>
                  <h2 className="text-xs font-black text-slate-700 uppercase tracking-widest m-0">Alış & Satış Faturaları</h2>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 mr-3">
                    <Checkbox
                      inputId="showOnlyOpenInvoices"
                      onChange={(e) => setShowOnlyOpenInvoices(e.checked)}
                      checked={showOnlyOpenInvoices}
                    />
                    <label htmlFor="showOnlyOpenInvoices" className="text-xs font-bold text-slate-600 cursor-pointer select-none">
                      Açık Faturaları Göster
                    </label>
                  </div>
                  <span className="p-input-icon-left w-full sm:w-64">
                    <i className="pi pi-search" />
                    <InputText
                      value={invoiceSearch}
                      onChange={(e) => setInvoiceSearch(e.target.value)}
                      placeholder="Fatura ara (No, Tarih...)"
                      className="p-inputtext-sm w-full font-bold  bg-white border-slate-200 rounded-xl"
                    />
                  </span>
                  <Tag value={`${filteredInvoices.length} FATURA`} className="!bg-indigo-900 text-[10px] ml-6 font-black" />
                </div>
              </div>
              <DataTable
                value={filteredInvoices}
                rows={25}
                paginator
                responsiveLayout="scroll"
                emptyMessage="Fatura kaydı bulunamadı."
                className="p-datatable-sm"
                globalFilter={invoiceSearch}
                globalFilterFields={["invoiceNo", "date"]}
                paginatorClassName="border-t border-slate-100 px-4 py-2 bg-slate-50"
                rowClassName={() => 'hover:bg-indigo-50/20 transition-colors'}
              >
                <Column
                  header="TÜR"
                  body={(rowData) => rowData.type === 'sales' ? (
                    <div className="flex items-center gap-1.5 text-indigo-600 bg-indigo-50/70 dark:bg-indigo-950/20 border border-indigo-100 dark:border-indigo-900/30 px-2 py-1 rounded-lg w-max font-bold text-[10px]">
                      <i className="pi pi-arrow-up-right"></i>
                      <span>SATIŞ</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5 text-orange-600 bg-orange-50/70 dark:bg-orange-950/20 border border-orange-100 dark:border-orange-900/30 px-2 py-1 rounded-lg w-max font-bold text-[10px]">
                      <i className="pi pi-arrow-down-left"></i>
                      <span>ALIŞ</span>
                    </div>
                  )}
                  className="w-24 text-[10px] font-bold"
                />
                <Column field="invoiceNo" header="FATURA NO" sortable className="text-slate-800 font-black text-xs" />
                <Column field="date" header="TARİH" sortable className="text-slate-500 text-[11px] font-bold" />
                <Column field="totalQuantity" header="ÜRÜN ADEDİ" sortable body={(rowData) => <span className="px-2 py-0.5 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-md font-black text-[10px] border border-slate-200 dark:border-slate-700">{rowData.totalQuantity}</span>} />
                <Column field="totalAmount" header="TOPLAM TUTAR" body={(rowData) => <span className="text-slate-900 text-xs font-black">₺{rowData.totalAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</span>} sortable />
                <Column field="paidAmount" header="ÖDENEN" body={(rowData) => <span className="text-emerald-600 text-xs font-black">₺{rowData.paidAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</span>} sortable />
                <Column field="remainingAmount" header="KALAN BORÇ" body={(rowData) => <span className={rowData.type === 'sales' ? "text-indigo-600 text-xs font-black" : "text-red-600 text-xs font-black"}>₺{rowData.remainingAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</span>} sortable />
                <Column field="dueDate" header="VADE TARİHİ" body={(rowData) => {
                  if (!rowData.dueDate) return <span className="text-slate-300">-</span>;
                  if (rowData.remainingAmount > 0.01) {
                    const parts = rowData.dueDate.split('.');
                    if (parts.length === 3) {
                      const due = new Date(parts[2], parts[1] - 1, parts[0]);
                      const today = new Date();
                      due.setHours(0, 0, 0, 0);
                      today.setHours(0, 0, 0, 0);
                      const diffTime = today.getTime() - due.getTime();
                      if (diffTime > 0) {
                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                        return (
                          <span 
                            className="text-rose-600 text-xs font-black cursor-help border-b border-dotted border-rose-400" 
                            title={`Vade Tarihi: ${rowData.dueDate}`}
                          >
                            {diffDays} Gün Gecikti
                          </span>
                        );
                      }
                    }
                  }
                  return <span className="text-orange-600 text-xs font-black">{rowData.dueDate}</span>;
                }} sortable />
                <Column
                  header="İŞLEMLER"
                  className="w-40"
                  body={(rowData) => rowData.type === 'sales' ? (
                    <div className="flex items-center gap-1">
                      {rowData.remainingAmount > 0.01 && (
                        <Button
                          icon="pi pi-plus-circle"
                          className="p-button-text p-button-success hover:bg-emerald-50 rounded-xl w-8 h-8 p-0"
                          tooltip="Tahsilat Al"
                          tooltipOptions={{ position: 'top' }}
                          onClick={() => {
                            setSelectedReceivableId(rowData.receivableDueItemId);
                            setPaymentAmount(rowData.remainingAmount);
                            setPaymentDescription(`${rowData.invoiceNo} nolu faturaya istinaden tahsilat`);
                            setShowPaymentDialog(true);
                          }}
                        />
                      )}
                      {customer.relatedSupplierId && rowData.remainingAmount > 0.01 && (
                        <>
                          <Button
                            icon="pi pi-arrow-h"
                            className="p-button-text p-button-info hover:bg-blue-50 rounded-xl w-8 h-8 p-0"
                            tooltip="Karşı Fatura ile Mahsup Et"
                            tooltipOptions={{ position: 'top' }}
                            onClick={() => {
                              setOffsetSourceInvoice(rowData);
                              setOffsetTargetInvoice(null);
                              setOffsetAmount(rowData.remainingAmount);
                              setOffsetMode('sales');
                              setShowOffsetDialog(true);
                            }}
                          />
                          <Button
                            icon="pi pi-sliders-h"
                            className="p-button-text p-button-help hover:bg-purple-50 rounded-xl w-8 h-8 p-0"
                            tooltip="Otomatik Mahsup Et (FIFO)"
                            tooltipOptions={{ position: 'top' }}
                            onClick={() => {
                              confirmDialog({
                                message: `${rowData.invoiceNo} nolu satış faturası için otomatik mahsuplaşma başlatılsın mı? Bu işlem FIFO yöntemini kullanarak karşı taraftaki en eski vadeli alış faturalarını kapatacaktır.`,
                                header: 'Otomatik Mahsuplaşma',
                                icon: 'pi pi-exclamation-triangle',
                                acceptLabel: 'Evet, Mahsup Et',
                                rejectLabel: 'Vazgeç',
                                accept: () => handleAutoOffset(rowData, 'sales')
                              });
                            }}
                          />
                        </>
                      )}
                      <Button
                        icon="pi pi-refresh"
                        className="p-button-rounded p-button-text p-button-warning hover:bg-orange-50 w-8 h-8 p-0"
                        tooltip="Faturayı İade Et"
                        tooltipOptions={{ position: 'top' }}
                        onClick={() => {
                          setSelectedReturnRow(rowData);
                          handleReturnProduct(rowData, null, true);
                        }}
                      />
                      <Button
                        icon="pi pi-print"
                        className="p-button-text p-button-success hover:bg-emerald-50 rounded-xl w-8 h-8 p-0"
                        tooltip="Yazdır"
                        tooltipOptions={{ position: 'top' }}
                        onClick={(e) => {
                          activeSaleIdRef.current = rowData.id;
                          setActiveSaleId(rowData.id);
                          printMenuRef.current.toggle(e);
                        }}
                      />
                      <Button
                        icon="pi pi-search"
                        className="p-button-rounded p-button-text p-button-secondary hover:bg-slate-100 w-8 h-8 p-0"
                        tooltip="Detay"
                        tooltipOptions={{ position: 'top' }}
                        onClick={() => handleShowSaleDetail(rowData.id)}
                      />
                      <Button
                        icon="pi pi-pencil"
                        className="p-button-rounded p-button-text p-button-info hover:bg-blue-50 w-8 h-8 p-0"
                        tooltip="Düzenle"
                        tooltipOptions={{ position: 'top' }}
                        onClick={() => navigate(`/sales/edit/${rowData.id}`)}
                      />
                    </div>
                  ) : (
                    <div className="flex items-center gap-1">
                      {customer.relatedSupplierId && rowData.remainingAmount > 0.01 && (
                        <>
                          <Button
                            icon="pi pi-arrow-h"
                            className="p-button-text p-button-info hover:bg-blue-50 rounded-xl w-8 h-8 p-0"
                            tooltip="Karşı Fatura ile Mahsup Et"
                            tooltipOptions={{ position: 'top' }}
                            onClick={() => {
                              setOffsetSourceInvoice(rowData);
                              setOffsetTargetInvoice(null);
                              setOffsetAmount(rowData.remainingAmount);
                              setOffsetMode('purchase');
                              setShowOffsetDialog(true);
                            }}
                          />
                          <Button
                            icon="pi pi-sliders-h"
                            className="p-button-text p-button-help hover:bg-purple-50 rounded-xl w-8 h-8 p-0"
                            tooltip="Otomatik Mahsup Et (FIFO)"
                            tooltipOptions={{ position: 'top' }}
                            onClick={() => {
                              confirmDialog({
                                message: `${rowData.invoiceNo} nolu alış faturası için otomatik mahsuplaşma başlatılsın mı? Bu işlem FIFO yöntemini kullanarak karşı taraftaki en eski vadeli satış faturalarını kapatacaktır.`,
                                header: 'Otomatik Mahsuplaşma',
                                icon: 'pi pi-exclamation-triangle',
                                acceptLabel: 'Evet, Mahsup Et',
                                rejectLabel: 'Vazgeç',
                                accept: () => handleAutoOffset(rowData, 'purchase')
                              });
                            }}
                          />
                        </>
                      )}
                      <Button
                        icon="pi pi-search"
                        className="p-button-rounded p-button-text p-button-secondary hover:bg-slate-100 w-8 h-8 p-0"
                        tooltip="Detay"
                        tooltipOptions={{ position: 'top' }}
                        onClick={() => navigate(`/suppliers/purchase/edit/${rowData.id}`)}
                      />
                    </div>
                  )}
                />
              </DataTable>
            </TabPanel>
            <TabPanel header="İadeler" leftIcon="pi pi-refresh mr-2">
              <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-orange-600 w-1.5 h-5 rounded-full"></div>
                  <h2 className="text-xs font-black text-slate-700 uppercase tracking-widest m-0">Alınan İadeler</h2>
                </div>
                <Tag value={`${returns.length} İADE`} className="!bg-orange-900 text-[10px] font-black" />
              </div>
              <DataTable
                value={returns}
                rows={25}
                paginator
                responsiveLayout="scroll"
                emptyMessage="İade kaydı bulunamadı."
                className="p-datatable-sm"
                paginatorClassName="border-t border-slate-100 px-4 py-2 bg-slate-50"
                rowClassName={() => 'hover:bg-orange-50/20 transition-colors'}
              >
                <Column field="returnDate" header="TARİH" sortable body={(rowData) => new Date(rowData.returnDate).toLocaleDateString('tr-TR')} className="text-slate-500 text-[11px] font-bold" />
                <Column field="productName" header="ÜRÜN ADI" sortable className="text-slate-800 font-black text-xs" />
                <Column field="quantity" header="ADET" sortable body={(rowData) => <span className="px-2 py-0.5 bg-orange-50 dark:bg-orange-950/40 text-orange-700 dark:text-orange-300 rounded-md font-black text-[10px] border border-orange-200 dark:border-orange-900/30">{rowData.quantity}</span>} />
                <Column field="unitPrice" header="B. FİYAT" body={(rowData) => <span className="text-slate-600 text-xs font-bold">₺{rowData.unitPrice.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</span>} />
                <Column field="totalAmount" header="TOPLAM" body={(rowData) => <span className="text-slate-900 text-xs font-black">₺{rowData.totalAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</span>} />
                <Column field="description" header="AÇIKLAMA" className="text-slate-500 text-[11px]" />
              </DataTable>
            </TabPanel>


          </TabView>
        </div>

        {/* Sağ Taraf: Yaklaşanlar ve Tahsilat Geçmişi */}
        <div className="lg:col-span-4 bg-white shadow-xl border border-slate-200 rounded-3xl overflow-hidden flex flex-col h-full">
          <TabView className="customer-detail-tabs flex-1 flex flex-col">
            <TabPanel header="Geçmiş" leftIcon="pi pi-history mr-2">
              <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
                <h2 className="text-[10px] font-black text-slate-500 uppercase tracking-widest m-0">Tahsilat Geçmişi</h2>
                <Tag value={`${payments.length} İŞLEM`} className="bg-emerald-600 text-[9px] font-black" />
              </div>
              <DataTable
                value={payments}
                rows={25}
                paginator
                responsiveLayout="scroll"
                emptyMessage="Tahsilat kaydı bulunamadı."
                className="p-datatable-sm"
                paginatorClassName="border-t border-slate-100 px-4 py-1 bg-slate-50"
                rowClassName={() => 'hover:bg-emerald-50/20 transition-colors'}
              >
                <Column field="date" header="TARİH" sortable className="text-slate-500 text-[10px] font-bold" />
                <Column field="paymentType" header="YÖNTEM" sortable body={(rowData) => (
                  <div className="flex flex-col">
                    <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase w-fit ${rowData.paymentType === 'Bakiye Düzeltme' ? '!bg-orange-100 text-orange-700' : '!bg-emerald-100 text-emerald-700'}`}>
                      {rowData.paymentType}
                    </span>
                    {rowData.paymentType === "Tahsilat" && rowData.description && (
                      <span className="text-[8px] text-slate-400 font-bold mt-0.5 italic">{rowData.description.split('\n')[0]}</span>
                    )}
                  </div>
                )} />
                <Column field="amount" header="TUTAR" body={(rowData) => <span className={`font-black text-xs ${rowData.amount < 0 ? 'text-red-600' : 'text-slate-900'}`}>₺{rowData.amount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</span>} sortable />
                <Column header="AKS" body={(rowData) => (
                  <div className="flex items-center gap-1">
                    {rowData.paymentType === "Tahsilat" && (
                      <Button
                        icon="pi pi-print"
                        className="p-button-rounded p-button-success p-button-text hover:bg-emerald-50 w-8 h-8 p-0"
                        onClick={() => handlePrintCollection(rowData.id)}
                        tooltip="Yazdır"
                        tooltipOptions={{ position: 'top' }}
                      />
                    )}
                    {actionBodyTemplate(rowData)}
                  </div>
                )} className="w-20" />
              </DataTable>
            </TabPanel>

            <TabPanel header="Yaklaşanlar" leftIcon="pi pi-calendar-clock mr-2">
              <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
                <h2 className="text-[10px] font-black text-slate-500 uppercase tracking-widest m-0">Bekleyen Alacaklar</h2>
                <Tag value={`${upcomingPayments.length} KALEM`} className="bg-orange-600 text-[9px] font-black" />
              </div>
              <DataTable
                value={upcomingPayments}
                rows={10}
                paginator
                responsiveLayout="scroll"
                emptyMessage="Bekleyen tahsilat bulunmuyor."
                className="p-datatable-sm"
                paginatorClassName="border-t border-slate-100 px-4 py-1 bg-slate-50"
                rowClassName={() => 'hover:bg-orange-50/20 transition-colors'}
              >
                <Column field="dueDate" header="VADE" body={(r) => <span className="font-bold text-slate-700 text-[11px]">{r.dueDate}</span>} />
                <Column field="remainingAmount" header="KALAN" body={(r) => <span className="font-black text-orange-600 text-xs">₺{r.remainingAmount.toLocaleString("tr-TR")}</span>} />
                <Column field="description" header="AÇIKLAMA" body={(r) => <span className="text-[10px] text-slate-400">{r.description}</span>} />
              </DataTable>
            </TabPanel>
          </TabView>
        </div>
      </div>

      {/* Bakiye Düzenleme Dialog */}
      {/* Tahsilat Al Dialog */}
      <Dialog
        header={
          <div className="flex items-center gap-3">
            <div className="!bg-emerald-100 dark:!bg-emerald-950/30 p-2 rounded-lg">
              <i className="pi pi-plus-circle text-emerald-600 dark:text-emerald-400 text-xl"></i>
            </div>
            <div>
              <h3 className="text-lg font-black text-slate-900 m-0">Tahsilat Al</h3>
              <p className="text-xs text-slate-500 font-medium m-0">Müşteriden ödeme girişi yapın</p>
            </div>
          </div>
        }
        visible={showPaymentDialog}
        style={{ width: '450px' }}
        breakpoints={{ '960px': '85vw', '641px': '95vw' }}
        onHide={() => {
          setShowPaymentDialog(false);
          setPaymentType("Nakit");
          setSelectedAccountId(null);
          setSelectedSupplierId(null);
        }}
        className="p-fluid"
        appendTo={mounted ? document.body : "self"}
        blockScroll={true}
        footer={(
          <div className="flex gap-2">
            <Button label="Vazgeç" icon="pi pi-times" className="p-button-text p-button-secondary font-bold" onClick={() => {
              setShowPaymentDialog(false);
              setPaymentType("Nakit");
              setSelectedAccountId(null);
              setSelectedSupplierId(null);
            }} />
            <Button label="Tahsilat Kaydet" icon="pi pi-check" className="!bg-emerald-600 border-none font-black" onClick={handleReceivePayment} loading={submitting} disabled={paymentAmount <= 0} />
          </div>
        )}
      >
        <div className="space-y-4 pt-6 pb-6">
          <div className="field">
            <label className="text-[11px] font-black text-slate-500 uppercase tracking-wider block mb-1.5 ml-1">Ödeme Tutarı (₺)</label>
            <InputNumber
              value={paymentAmount}
              onValueChange={(e) => setPaymentAmount(e.value)}
              mode="decimal"
              minFractionDigits={2}
              placeholder="0,00"
              autoFocus
              className="w-full"
              inputClassName="font-black text-lg py-3 rounded-xl border-slate-200 focus:ring-2 focus:ring-emerald-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4 pb-5">
            <div className="field col-span-2">
              <label className="text-[11px] font-black text-slate-500 uppercase tracking-wider block mb-1.5 ml-1">Ödeme Yöntemi</label>
              <Dropdown
                value={paymentType}
                options={paymentTypes}
                onChange={(e) => {
                  setPaymentType(e.value);
                  setSelectedAccountId(null);
                  setSelectedSupplierId(null);
                }}
                placeholder="Seçiniz"
                className="rounded-xl border-slate-200"
                appendTo="self"
              />
            </div>
            {(filteredAccounts.length > 0 || paymentType === "Sanal POS") && (
              <div className="field col-span-1">
                <label className="text-[11px] font-black text-slate-500 uppercase tracking-wider block mb-1.5 ml-1">İşlem Yapılacak Hesap</label>
                <Dropdown
                  value={selectedAccountId}
                  options={filteredAccounts}
                  optionLabel="name"
                  optionValue="id"
                  onChange={(e) => handleAccountChange(e.value)}
                  placeholder="Hesap seçin..."
                  className="rounded-xl border-slate-200"
                  appendTo={"self"}
                />
              </div>
            )}
            {paymentType === "Sanal POS" && (
              <div className="field col-span-1">
                <label className="text-[11px] font-black text-slate-500 uppercase tracking-wider block mb-1.5 ml-1">Tedarikçi</label>
                <Dropdown
                  value={selectedSupplierId}
                  options={suppliers}
                  optionLabel="name"
                  optionValue="id"
                  filter
                  onChange={(e) => handleSupplierChange(e.value)}
                  placeholder="Tedarikçi seçin..."
                  className="rounded-xl border-slate-200"
                  appendTo={"self"}
                />
              </div>
            )}

            <div className="field">
              <label className="text-[11px] font-black text-slate-500 uppercase tracking-wider block mb-1.5 ml-1">İşlem Tarihi</label>
              <Calendar
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.value)}
                dateFormat="dd.mm.yy"
                locale="tr"
                showIcon
                appendTo={document.body}
                className="w-full"
                inputClassName="rounded-xl border-slate-200 py-2 font-bold"
              />
            </div>
          </div>

          {paymentType === "Çek" && (
            <div className="!bg-blue-50/50 dark:!bg-blue-950/20 p-4 rounded-2xl border border-blue-100 dark:border-blue-900/30 space-y-3 animate-fade-in">
              <span className="text-[10px] font-black text-blue-600 dark:text-blue-400 uppercase tracking-widest block mb-1">ÇEK BİLGİLERİ</span>
              <div className="grid grid-cols-2 gap-3">
                <div className="field">
                  <label className="text-[10px] font-bold text-slate-600">Banka</label>
                  <InputText value={checkDetails.bankName} onChange={e => setCheckDetails({ ...checkDetails, bankName: e.target.value })} className="p-inputtext-sm" />
                </div>
                <div className="field">
                  <label className="text-[10px] font-bold text-slate-600">Şube</label>
                  <InputText value={checkDetails.bankBranch} onChange={e => setCheckDetails({ ...checkDetails, bankBranch: e.target.value })} className="p-inputtext-sm" />
                </div>
              </div>
              <div className="field">
                <label className="text-[10px] font-bold text-slate-600">Çek Numarası</label>
                <InputText value={checkDetails.checkNumber} onChange={e => setCheckDetails({ ...checkDetails, checkNumber: e.target.value })} className="p-inputtext-sm" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="field">
                  <label className="text-[10px] font-bold text-slate-600">Vade Tarihi</label>
                  <Calendar value={checkDetails.dueDate} onChange={e => setCheckDetails({ ...checkDetails, dueDate: e.value })} dateFormat="dd.mm.yy" locale="tr" showIcon appendTo={document.body} className="p-inputtext-sm" />
                </div>
                <div className="field">
                  <label className="text-[10px] font-bold text-slate-600">Keşideci</label>
                  <InputText value={checkDetails.drawer} onChange={e => setCheckDetails({ ...checkDetails, drawer: e.target.value })} className="p-inputtext-sm" />
                </div>
              </div>
            </div>
          )}

          {paymentType === "Senet" && (
            <div className="bg-orange-50/50 dark:!bg-orange-950/20 p-4 rounded-2xl border border-orange-100 dark:border-orange-900/30 space-y-3 animate-fade-in">
              <span className="text-[10px] font-black text-orange-600 dark:text-orange-400 uppercase tracking-widest block mb-1">SENET BİLGİLERİ</span>
              <div className="field">
                <label className="text-[10px] font-bold text-slate-600">Vade Tarihi</label>
                <Calendar value={promissoryDetails.dueDate} onChange={e => setPromissoryDetails({ ...promissoryDetails, dueDate: e.value })} dateFormat="dd.mm.yy" locale="tr" showIcon appendTo={document.body} className="p-inputtext-sm" />
              </div>
              <div className="field">
                <label className="text-[10px] font-bold text-slate-600">Borçlu (Düzenleyen)</label>
                <InputText value={promissoryDetails.drawer} onChange={e => setPromissoryDetails({ ...promissoryDetails, drawer: e.target.value })} className="p-inputtext-sm" />
              </div>
              <div className="field">
                <label className="text-[10px] font-bold text-slate-600">Kefil</label>
                <InputText value={promissoryDetails.guarantor} onChange={e => setPromissoryDetails({ ...promissoryDetails, guarantor: e.target.value })} className="p-inputtext-sm" />
              </div>
            </div>
          )}

          <div className="field">
            <label className="text-[11px] font-black text-slate-500 uppercase tracking-wider block mb-1.5 ml-1">Açıklama</label>
            <InputText
              value={paymentDescription}
              onChange={(e) => setPaymentDescription(e.target.value)}
              placeholder="Ek notlar..."
              className="rounded-xl border-slate-200"
            />
          </div>
        </div>
      </Dialog>

      {/* Ekstre Seçim Dialog */}
      <Dialog
        header={
          <div className="flex items-center gap-3">
            <div className="bg-slate-100 dark:bg-slate-800 p-2 rounded-lg">
              <i className="pi pi-file-pdf text-slate-600 dark:text-slate-300 text-xl"></i>
            </div>
            <div>
              <h3 className="text-lg font-black text-slate-900 dark:text-white m-0">Ekstre Seçenekleri</h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 font-medium m-0">Yazdırmak veya indirmek istediğiniz formatı seçin</p>
            </div>
          </div>
        }
        visible={showStatementDialog}
        style={{ width: '450px' }}
        onHide={() => setShowStatementDialog(false)}
        className="p-fluid"
        appendTo={mounted ? document.body : "self"}
      >
        <div className="py-2">
          <TabView>
            <TabPanel header={
              <div className="flex items-center gap-2">
                <i className="pi pi-print"></i>
                <span>Ekstre Fişi</span>
              </div>
            }>
              <div className="flex flex-col gap-3 pt-3">
                <div className="bg-blue-50 dark:bg-blue-950/20 p-3 rounded-xl border border-blue-100 dark:border-blue-900/30 mb-2">
                  <p className="text-xs font-semibold text-blue-800 dark:text-blue-300 m-0">
                    80mm Termal yazıcılar için rulo fiş formatında yazdırın.
                  </p>
                </div>
                <Button
                  label="Sade Ekstre (Özet)"
                  icon="pi pi-file"
                  className="!bg-blue-600 border-none font-black !py-3 !rounded-xl text-white font-bold"
                  onClick={() => {
                    setShowStatementDialog(false);
                    statementReceiptRef.current.print(fullDetail, 'statement_simple');
                  }}
                />
                <Button
                  label="Detaylı Ekstre (Tüm Liste)"
                  icon="pi pi-list"
                  className="!bg-slate-800 border-none font-black !py-3 !rounded-xl text-white font-bold"
                  onClick={() => {
                    setShowStatementDialog(false);
                    statementReceiptRef.current.print(fullDetail, 'statement_detailed');
                  }}
                />
              </div>
            </TabPanel>
            <TabPanel header={
              <div className="flex items-center gap-2">
                <i className="pi pi-file-pdf"></i>
                <span>Ekstre Dökümü</span>
              </div>
            }>
              <div className="flex flex-col gap-4 pt-3">
                <div className="bg-emerald-50 dark:bg-emerald-950/20 p-3 rounded-xl border border-emerald-100 dark:border-emerald-900/30">
                  <p className="text-xs font-semibold text-emerald-800 dark:text-emerald-300 m-0">
                    A4 formatında PDF ekstre dökümü seçenekleri.
                  </p>
                </div>
                
                {/* 1) Tarih Seçimi */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-bold text-slate-600 dark:text-slate-400">Başlangıç Tarihi</label>
                    <Calendar 
                      value={statementStartDate} 
                      onChange={(e) => setStatementStartDate(e.value)} 
                      dateFormat="dd.mm.yy" 
                      showIcon 
                      placeholder="Seçiniz"
                      className="p-inputtext-sm"
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-bold text-slate-600 dark:text-slate-400">Bitiş Tarihi</label>
                    <Calendar 
                      value={statementEndDate} 
                      onChange={(e) => setStatementEndDate(e.value)} 
                      dateFormat="dd.mm.yy" 
                      showIcon 
                      placeholder="Seçiniz"
                      className="p-inputtext-sm"
                    />
                  </div>
                </div>

                {/* 2) & 3) Seçenekler */}
                <div className="flex flex-col gap-2 bg-slate-50 dark:bg-slate-900/50 p-3 rounded-xl border border-slate-100 dark:border-slate-800">
                  <div className="flex items-center gap-2">
                    <Checkbox 
                      inputId="showProductsOpt" 
                      checked={statementShowProducts} 
                      onChange={(e) => setStatementShowProducts(e.checked)} 
                    />
                    <label htmlFor="showProductsOpt" className="text-xs font-bold text-slate-700 dark:text-slate-300 cursor-pointer">
                      Faturaya ait ürünleri göster (Detaylı)
                    </label>
                  </div>

                  {customer?.relatedSupplierId && (
                    <div className="flex items-center gap-2 mt-2 pt-2 border-t border-slate-200 dark:border-slate-800">
                      <Checkbox 
                        inputId="includePurchasesOpt" 
                        checked={statementIncludePurchases} 
                        onChange={(e) => setStatementIncludePurchases(e.checked)} 
                      />
                      <label htmlFor="includePurchasesOpt" className="text-xs font-bold text-slate-700 dark:text-slate-300 cursor-pointer">
                        Alış Faturalarını Dahil Et (Konsolide Ekstre)
                      </label>
                    </div>
                  )}
                </div>

                {/* 4) Banka Hesapları */}
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-bold text-slate-600 dark:text-slate-400">Döküme Eklenecek Banka Hesapları</label>
                  <div className="max-h-[120px] overflow-y-auto border border-slate-200 dark:border-slate-800 rounded-xl p-2 flex flex-col gap-2 bg-white dark:bg-slate-950">
                    {financialAccounts.filter(a => a.accountType === 2).length === 0 ? (
                      <span className="text-xs text-slate-400 italic p-1">Kayıtlı banka hesabı bulunamadı.</span>
                    ) : (
                      financialAccounts.filter(a => a.accountType === 2).map((acc) => (
                        <div key={acc.id} className="flex items-center gap-2">
                          <Checkbox 
                            inputId={`bank_acc_${acc.id}`} 
                            value={acc}
                            checked={statementSelectedBanks.some(b => b.id === acc.id)}
                            onChange={(e) => {
                              if (e.checked) {
                                setStatementSelectedBanks([...statementSelectedBanks, acc]);
                              } else {
                                setStatementSelectedBanks(statementSelectedBanks.filter(b => b.id !== acc.id));
                              }
                            }}
                          />
                          <label htmlFor={`bank_acc_${acc.id}`} className="text-xs text-slate-700 dark:text-slate-300 cursor-pointer">
                            <strong>{acc.name}</strong> {acc.accountNumber ? `(${acc.accountNumber})` : ''}
                          </label>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Download Button */}
                <Button
                  label="PDF Dökümü Oluştur (A4 PDF)"
                  icon="pi pi-file-pdf"
                  className="!bg-emerald-600 border-none font-black !py-3 !rounded-xl text-white font-bold mt-2"
                  onClick={async () => {
                    setShowStatementDialog(false);
                    try {
                      const requestBody = {
                        startDate: statementStartDate ? statementStartDate.toISOString() : null,
                        endDate: statementEndDate ? statementEndDate.toISOString() : null,
                        includePurchases: statementIncludePurchases && !!customer?.relatedSupplierId,
                        showProducts: statementShowProducts
                      };
                      const res = await customerService.getCustomerStatement(customer.id, requestBody);
                      if (res.data && res.data.success) {
                        statementReportRef.current.downloadPDF(
                          res.data.data, 
                          statementShowProducts, 
                          statementStartDate, 
                          statementEndDate, 
                          statementSelectedBanks,
                          requestBody.includePurchases
                        );
                      } else {
                        toast.current.show({ severity: 'error', summary: 'Hata', detail: res.data?.message || 'Ekstre alınamadı', life: 3000 });
                      }
                    } catch (err) {
                      toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Ekstre indirilirken hata oluştu', life: 3000 });
                    }
                  }}
                />
              </div>
            </TabPanel>
          </TabView>
          <div className="flex justify-end mt-4 pt-2 border-t border-slate-100 dark:border-slate-800">
            <Button
              label="Kapat"
              icon="pi pi-times"
              className="p-button-text p-button-secondary font-bold max-w-[100px]"
              onClick={() => setShowStatementDialog(false)}
            />
          </div>
        </div>
      </Dialog>

      {/* İskonto Yap Dialog */}
      <Dialog
        header={
          <div className="flex items-center gap-3">
            <div className="bg-rose-100 dark:bg-rose-950/30 p-2 rounded-lg">
              <i className="pi pi-percentage text-rose-600 dark:text-rose-400 text-xl"></i>
            </div>
            <div>
              <h3 className="text-lg font-black text-slate-900 m-0">İskonto Yap</h3>
              <p className="text-xs text-slate-500 font-medium m-0">Müşterinin güncel borcuna iskonto uygulayın</p>
            </div>
          </div>
        }
        visible={showDiscountDialog}
        style={{ width: '400px' }}
        onHide={() => setShowDiscountDialog(false)}
        className="p-fluid"
        appendTo={mounted ? document.body : "self"}
        footer={(
          <div className="flex gap-2">
            <Button label="Vazgeç" icon="pi pi-times" className="p-button-text p-button-secondary font-bold" onClick={() => setShowDiscountDialog(false)} />
            <Button label="İskonto Uygula" icon="pi pi-check" className="!bg-rose-600 border-none font-black" onClick={handleApplyDiscount} loading={submitting} disabled={discountAmount <= 0} />
          </div>
        )}
      >
        <div className="space-y-4 pt-4">
          <div className="field">
            <label className="text-[11px] font-black text-slate-500 uppercase tracking-wider block mb-1.5 ml-1">İskonto Tutarı (₺)</label>
            <InputNumber
              value={discountAmount}
              onValueChange={(e) => setDiscountAmount(e.value)}
              mode="decimal"
              minFractionDigits={2}
              placeholder="0,00"
              autoFocus
              className="w-full"
              inputClassName="font-black text-lg py-3 rounded-xl border-slate-200 focus:ring-2 focus:ring-rose-500"
            />
          </div>
          <div className="field">
            <label className="text-[11px] font-black text-slate-500 uppercase tracking-wider block mb-1.5 ml-1">Açıklama</label>
            <InputText
              value={discountDescription}
              onChange={(e) => setDiscountDescription(e.target.value)}
              placeholder="İskonto sebebi..."
              className="rounded-xl border-slate-200"
            />
          </div>
        </div>
      </Dialog>

      <Dialog
        header="Manuel Bakiye Düzenleme"
        visible={showAdjustmentDialog}
        style={{ width: '400px' }}
        onHide={() => setShowAdjustmentDialog(false)}
        className="p-fluid"
        appendTo={mounted ? document.body : "self"}
        footer={(
          <div className="flex gap-2">
            <Button label="Vazgeç" icon="pi pi-times" className="p-button-text" onClick={() => setShowAdjustmentDialog(false)} />
            <Button label="Kaydet" icon="pi pi-check" className="p-button-primary" onClick={handleAdjustBalance} loading={submitting} />
          </div>
        )}
      >
        <div className="space-y-4 pt-2">
          <p className="text-xs text-slate-500 font-medium">Bu işlem müşterinin güncel bakiyesine doğrudan ekleme veya çıkarma yapar. (+ değerler borç ekler, - değerler alacak ekler)</p>
          <div className="field">
            <label className="text-xs font-bold text-slate-600">Düzenleme Tutarı (₺)</label>
            <InputNumber value={adjustmentAmount} onValueChange={e => setAdjustmentAmount(e.value)} mode="decimal" minFractionDigits={2} autoFocus />
          </div>
          <div className="field">
            <label className="text-xs font-bold text-slate-600">Açıklama</label>
            <InputText value={adjustmentDescription} onChange={e => setAdjustmentDescription(e.target.value)} placeholder="Örn: Açılış Bakiyesi" />
          </div>
        </div>
      </Dialog>

      {/* Ödeme Düzenleme Dialog */}
      <Dialog
        header="İşlem Düzenle"
        visible={showEditPaymentDialog}
        style={{ width: '400px' }}
        onHide={() => setShowEditPaymentDialog(false)}
        className="p-fluid"
        appendTo={mounted ? document.body : "self"}
        footer={(
          <div className="flex gap-2">
            <Button label="Vazgeç" icon="pi pi-times" className="p-button-text" onClick={() => setShowEditPaymentDialog(false)} />
            <Button label="Güncelle" icon="pi pi-check" className="p-button-primary" onClick={handleUpdatePayment} loading={submitting} />
          </div>
        )}
      >
        {editingPayment && (
          <div className="space-y-4 pt-2">
            <div className="field">
              <label className="text-xs font-bold text-slate-600">Tutar (₺)</label>
              <InputNumber value={editingPayment.amount} onValueChange={e => setEditingPayment({ ...editingPayment, amount: e.value })} mode="decimal" minFractionDigits={2} />
            </div>
            <div className="field">
              <label className="text-xs font-bold text-slate-600">Ödeme Yöntemi</label>
              <Dropdown
                value={editingPayment.paymentType}
                options={paymentTypes}
                onChange={e => setEditingPayment({ ...editingPayment, paymentType: e.value })}
                placeholder="Yöntem Seç"
                appendTo="self"
              />
            </div>
            {filteredAccounts.length > 0 && (
              <div className="field">
                <label className="text-xs font-bold text-slate-600">İşlem Yapılacak Hesap</label>
                <Dropdown
                  value={editingPayment.financialAccountId}
                  options={filteredAccounts}
                  optionLabel="name"
                  optionValue="id"
                  onChange={(e) => setEditingPayment({ ...editingPayment, financialAccountId: e.value })}
                  placeholder="Hesap seçin..."
                  className="rounded-xl border-slate-200"
                />
              </div>
            )}
            <div className="field">
              <label className="text-xs font-bold text-slate-600">Açıklama</label>
              <InputText value={editingPayment.description || ""} onChange={e => setEditingPayment({ ...editingPayment, description: e.target.value })} />
            </div>
          </div>
        )}
      </Dialog>

      {/* Satış Detay Popup */}
      <Dialog
        header={
          <div className="flex items-center gap-3">
            <div className="bg-blue-100 dark:bg-blue-950/30 p-2 rounded-lg">
              <i className="pi pi-file text-blue-600 dark:text-blue-400 text-xl"></i>
            </div>
            <div>
              <h3 className="text-lg font-black text-slate-900 m-0">Satış Detayı</h3>
              <p className="text-xs text-slate-500 font-medium m-0">
                {selectedSaleDetail ? `Fatura No: ${selectedSaleDetail.saleNo}` : 'Yükleniyor...'}
              </p>
            </div>
          </div>
        }
        visible={saleDetailVisible}
        style={{ width: '700px' }}
        onHide={() => { setSaleDetailVisible(false); setSelectedSaleDetail(null); }}
        className="p-fluid"
        appendTo={mounted ? document.body : "self"}
      >
        {loadingSaleDetail ? (
          <div className="flex flex-col items-center justify-center py-10">
            <i className="pi pi-spin pi-spinner text-3xl text-blue-600"></i>
            <p className="mt-2 text-slate-500 font-bold">Detaylar yükleniyor...</p>
          </div>
        ) : selectedSaleDetail && (
          <div className="space-y-6 pt-4">
            {/* Ürünler Tablosu */}
            <div className="border border-slate-100 rounded-2xl overflow-hidden shadow-sm">
              <DataTable value={selectedSaleDetail.items} size="small" className="p-datatable-sm">
                <Column
                  field="productName"
                  header="ÜRÜN ADI"
                  className="font-bold text-xs"
                  body={(row) => (
                    <div className="flex flex-col">
                      <span>{row.productName}</span>
                      <span className="text-[9px] text-slate-400 font-medium">{row.warehouseName}</span>
                    </div>
                  )}
                />
                <Column field="quantity" header="ADET" body={(row) => <span className="font-black">{row.quantity}</span>} />
                <Column field="unitPrice" header="B. FİYAT" body={(row) => <span>₺{row.unitPrice.toLocaleString('tr-TR')}</span>} />
                <Column field="lineTotal" header="TOPLAM" body={(row) => <span className="font-black">₺{row.lineTotal.toLocaleString('tr-TR')}</span>} />
              </DataTable>
            </div>

            {/* Alt Bilgiler */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">ÖDEME BİLGİLERİ</span>
                <div className="space-y-2">
                  {selectedSaleDetail.payments && selectedSaleDetail.payments.length > 0 ? (
                    selectedSaleDetail.payments.map((p, idx) => (
                      <div key={idx} className="flex justify-between items-center text-xs">
                        <span className="font-bold text-slate-600">{p.method}</span>
                        <span className="font-black text-emerald-600">₺{p.amount.toLocaleString('tr-TR')}</span>
                      </div>
                    ))
                  ) : (
                    <p className="text-[10px] text-slate-400 italic">Ödeme kaydı bulunamadı.</p>
                  )}
                </div>
              </div>

              <div className="bg-slate-900 text-white p-4 rounded-2xl shadow-lg relative overflow-hidden">
                <div className="absolute top-0 right-0 w-20 h-20 bg-blue-500/10 rounded-full -mr-10 -mt-10 blur-xl"></div>
                <div className="relative z-10 space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-[10px] font-bold text-slate-400 uppercase">Ara Toplam</span>
                    <span className="text-xs font-bold">₺{selectedSaleDetail.subTotal.toLocaleString('tr-TR')}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[10px] font-bold text-red-400 uppercase">İskonto</span>
                    <span className="text-xs font-bold text-red-400">-₺{selectedSaleDetail.discountTotal.toLocaleString('tr-TR')}</span>
                  </div>
                  <div className="pt-2 border-t border-white/10 flex justify-between items-center">
                    <span className="text-[10px] font-black text-blue-400 uppercase">GENEL TOPLAM</span>
                    <span className="text-lg font-black text-white">₺{selectedSaleDetail.grandTotal.toLocaleString('tr-TR')}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[10px] font-bold text-emerald-400 uppercase">TOPLAM ÖDENEN</span>
                    <span className="text-sm font-black text-emerald-400">₺{selectedSaleDetail.paidTotal.toLocaleString('tr-TR')}</span>
                  </div>
                </div>
              </div>
            </div>

          </div>
        )}
      </Dialog>
      <Menu model={printMenuItems} popup ref={printMenuRef} appendTo={document.body} />
      <Menu model={detailActionsMenuItems} popup ref={detailActionsMenuRef} appendTo={document.body} />
      <SaleReceipt ref={receiptRef} />
      <CustomerStatementReceipt ref={statementReceiptRef} />
      <CustomerStatementReport ref={statementReportRef} />

      <Dialog
        header="Fatura Mahsuplaşma (Virman/Offset)"
        visible={showOffsetDialog}
        style={{ width: '500px' }}
        modal
        onHide={() => setShowOffsetDialog(false)}
        footer={(
          <div className="flex justify-end gap-2">
            <Button
              label="Vazgeç"
              icon="pi pi-times"
              className="p-button-text p-button-secondary !rounded-xl font-bold"
              onClick={() => setShowOffsetDialog(false)}
            />
            <Button
              label="Mahsup Et"
              icon="pi pi-check"
              loading={offsetSubmitting}
              className="p-button-primary !rounded-xl font-bold !bg-indigo-600 !border-indigo-600 shadow-md shadow-indigo-500/20"
              onClick={handleOffsetSubmit}
            />
          </div>
        )}
      >
        {offsetSourceInvoice && (
          <div className="flex flex-col gap-4 p-fluid">
            <div className="p-3 bg-slate-50 border border-slate-100 rounded-xl">
              <span className="text-[11px] font-black text-slate-500 uppercase tracking-widest block mb-1">Kaynak Fatura</span>
              <div className="flex items-center justify-between">
                <span className="text-xs font-black text-slate-800">
                  {offsetMode === 'sales' ? offsetSourceInvoice.saleNo : offsetSourceInvoice.invoiceNo}
                </span>
                <span className="text-xs font-bold text-slate-500">
                  Kalan: ₺{((offsetMode === 'sales' ? (offsetSourceInvoice.totalAmount - offsetSourceInvoice.paidAmount) : offsetSourceInvoice.remainingAmount)).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                </span>
              </div>
            </div>

            <div>
              <label className="text-xs font-black text-slate-600 block mb-2">Mahsup Edilecek Hedef Faturayı Seçin</label>
              <Dropdown
                value={offsetTargetInvoice}
                onChange={(e) => {
                  setOffsetTargetInvoice(e.value);
                  if (e.value) {
                    const sourceRemaining = offsetMode === 'sales'
                      ? (offsetSourceInvoice.totalAmount - offsetSourceInvoice.paidAmount)
                      : offsetSourceInvoice.remainingAmount;
                    const targetRemaining = offsetMode === 'sales'
                      ? e.value.remainingAmount
                      : (e.value.totalAmount - e.value.paidAmount);
                    setOffsetAmount(Math.min(sourceRemaining, targetRemaining));
                  }
                }}
                options={offsetMode === 'sales'
                  ? supplierInvoices.filter(inv => inv.remainingAmount > 0.01)
                  : invoices.filter(inv => inv.totalAmount - inv.paidAmount > 0.01)
                }
                optionLabel={(inv) => `${inv.saleNo || inv.invoiceNo} - ₺${(inv.remainingAmount || (inv.totalAmount - inv.paidAmount)).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} (Vade: ${inv.dueDate || 'Belirtilmemiş'})`}
                placeholder="Fatura seçin..."
                className="rounded-xl border-slate-200"
              />
            </div>

            {offsetTargetInvoice && (
              <div>
                <label className="text-xs font-black text-slate-600 block mb-2">Mahsup Tutarı (₺)</label>
                <InputNumber
                  value={offsetAmount}
                  onValueChange={(e) => setOffsetAmount(e.value || 0)}
                  mode="decimal"
                  min={0.01}
                  max={Math.min(
                    offsetMode === 'sales' ? (offsetSourceInvoice.totalAmount - offsetSourceInvoice.paidAmount) : offsetSourceInvoice.remainingAmount,
                    offsetMode === 'sales' ? offsetTargetInvoice.remainingAmount : (offsetTargetInvoice.totalAmount - offsetTargetInvoice.paidAmount)
                  )}
                  minFractionDigits={2}
                  maxFractionDigits={2}
                  className="rounded-xl"
                  inputClassName="font-black"
                />
              </div>
            )}
          </div>
        )}
      </Dialog>

      <Dialog
        header="Fatura Seçimi ile Mahsuplaşma"
        visible={showGeneralOffsetDialog}
        style={{ width: '850px' }}
        modal
        onHide={() => setShowGeneralOffsetDialog(false)}
        footer={(
          <div className="flex justify-end gap-2">
            <Button
              label="Vazgeç"
              icon="pi pi-times"
              className="p-button-text p-button-secondary !rounded-xl font-bold"
              onClick={() => setShowGeneralOffsetDialog(false)}
            />
            <Button
              label="Mahsup Et"
              icon="pi pi-sync"
              loading={offsetSubmitting}
              disabled={offsetSteps.length === 0}
              className="p-button-primary !rounded-xl font-bold !bg-purple-600 !border-purple-600 shadow-md shadow-purple-500/20"
              onClick={handleGeneralOffsetSubmit}
            />
          </div>
        )}
      >
        <div className="flex flex-col gap-4 p-fluid">
          <div className="p-3 bg-purple-50 dark:bg-purple-950/20 border border-purple-100 dark:border-purple-900/30 rounded-xl text-purple-900 dark:text-purple-300 text-xs font-bold leading-relaxed">
            Lütfen mahsup etmek istediğiniz müşteri (satış) faturalarını ve tedarikçi (alış) faturalarını tablolardan seçin. Sistem, seçtiğiniz faturaların bakiyelerini otomatik olarak eşleştirecektir.
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* SOL - Müşteri Satış Faturaları */}
            <div className="border border-slate-100 dark:border-slate-800 rounded-xl p-3 bg-white dark:bg-slate-950 shadow-sm">
              <span className="text-xs font-black text-slate-700 dark:text-slate-300 block mb-2 uppercase tracking-wide">Müşteri Faturaları (Satış)</span>
              <DataTable
                value={invoices.filter(inv => inv.totalAmount - inv.paidAmount > 0.01)}
                selection={selectedOffsetCustomerInvoices}
                onSelectionChange={(e) => setSelectedOffsetCustomerInvoices(e.value || [])}
                dataKey="id"
                responsiveLayout="scroll"
                className="p-datatable-sm text-[11px]"
                emptyMessage="Açık satış faturası bulunamadı."
                scrollable
                scrollHeight="200px"
              >
                <Column selectionMode="multiple" headerStyle={{ width: '3em' }}></Column>
                <Column field="saleNo" header="Fatura No" className="font-bold text-slate-800 dark:text-slate-200"></Column>
                <Column field="dueDate" header="Vade" className="text-slate-500" body={(r) => r.dueDate || '-'}></Column>
                <Column header="Kalan Bakiye" className="font-semibold text-right" body={(r) => `₺${(r.totalAmount - r.paidAmount).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`}></Column>
              </DataTable>
            </div>

            {/* SAĞ - Tedarikçi Alış Faturaları */}
            <div className="border border-slate-100 dark:border-slate-800 rounded-xl p-3 bg-white dark:bg-slate-950 shadow-sm">
              <span className="text-xs font-black text-slate-700 dark:text-slate-300 block mb-2 uppercase tracking-wide">Tedarikçi Faturaları (Alış)</span>
              <DataTable
                value={supplierInvoices.filter(inv => inv.remainingAmount > 0.01)}
                selection={selectedOffsetSupplierInvoices}
                onSelectionChange={(e) => setSelectedOffsetSupplierInvoices(e.value || [])}
                dataKey="id"
                responsiveLayout="scroll"
                className="p-datatable-sm text-[11px]"
                emptyMessage="Açık alış faturası bulunamadı."
                scrollable
                scrollHeight="200px"
              >
                <Column selectionMode="multiple" headerStyle={{ width: '3em' }}></Column>
                <Column field="invoiceNo" header="Fatura No" className="font-bold text-slate-800 dark:text-slate-200"></Column>
                <Column field="dueDate" header="Vade" className="text-slate-500" body={(r) => r.dueDate || '-'}></Column>
                <Column header="Kalan Bakiye" className="font-semibold text-right" body={(r) => `₺${r.remainingAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`}></Column>
              </DataTable>
            </div>
          </div>

          {offsetSteps.length > 0 && (
            <div className="mt-2">
              <label className="text-xs font-black text-slate-600 dark:text-slate-400 block mb-2">Mahsup Eşleştirme Önizlemesi</label>
              <div className="border border-slate-100 dark:border-slate-800 rounded-xl overflow-hidden max-h-48 overflow-y-auto bg-slate-50 dark:bg-slate-900/40">
                <table className="w-full text-xs text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 font-bold border-b border-slate-100 dark:border-slate-800">
                      <th className="p-2.5">Müşteri Faturası (Satış)</th>
                      <th className="p-2.5">Tedarikçi Faturası (Alış)</th>
                      <th className="p-2.5 text-right">Mahsup Tutarı</th>
                    </tr>
                  </thead>
                  <tbody>
                    {offsetSteps.map((step, idx) => (
                      <tr key={idx} className="border-b last:border-0 border-slate-100 dark:border-slate-800/50 bg-white dark:bg-slate-950 hover:bg-slate-50 dark:hover:bg-slate-900/30 transition-colors">
                        <td className="p-2.5 font-bold text-slate-700 dark:text-slate-300">{step.customerInvoice.saleNo}</td>
                        <td className="p-2.5 font-semibold text-slate-500 dark:text-slate-400">{step.supplierInvoice.invoiceNo}</td>
                        <td className="p-2.5 text-right font-black text-purple-600 dark:text-purple-400">
                          ₺{step.amount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-3 flex justify-between items-center p-3 bg-purple-100/50 dark:bg-purple-950/20 border border-purple-200/30 rounded-xl">
                <span className="text-xs font-bold text-purple-900 dark:text-purple-300">Toplam Mahsup Tutarı</span>
                <span className="text-sm font-black text-purple-700 dark:text-purple-400">
                  ₺{totalOffsetAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          )}
        </div>
      </Dialog>

      <Dialog
        header="Özel Ürün Fiyatları"
        visible={showAllSpecialPricesDialog}
        style={{ width: '750px' }}
        modal
        onHide={() => setShowAllSpecialPricesDialog(false)}
        footer={(
          <div className="flex justify-end gap-2">
            <Button
              label="Kapat"
              icon="pi pi-times"
              className="p-button-text p-button-secondary"
              onClick={() => setShowAllSpecialPricesDialog(false)}
            />
            <Button
              label="Yeni Özel Fiyat Tanımla"
              icon="pi pi-plus"
              className="p-button-success"
              onClick={() => setShowSpecialPriceDialog(true)}
            />
          </div>
        )}
      >
        {specialPricesLoading ? (
          <div className="flex flex-col items-center justify-center p-8">
            <i className="pi pi-spin pi-spinner text-3xl text-blue-600"></i>
            <p className="mt-2 text-slate-500 font-medium">Özel fiyatlar yükleniyor...</p>
          </div>
        ) : (
          <DataTable
            value={specialPrices}
            responsiveLayout="scroll"
            emptyMessage="Bu müşteriye tanımlı özel ürün fiyatı bulunamadı."
            className="p-datatable-sm"
            rowClassName={() => 'hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors'}
          >
            <Column field="productName" header="ÜRÜN / HİZMET" className="font-bold text-slate-700 dark:text-slate-300" />
            <Column
              field="priceInclVat"
              header="BİRİM FİYAT (KDV DAHİL)"
              body={(row) => <span className="font-black text-slate-900 dark:text-slate-100">{row.priceInclVat?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺</span>}
            />
            <Column
              field="priceExclVat"
              header="BİRİM FİYAT (KDV HARİÇ)"
              body={(row) => <span className="text-slate-500">{row.priceExclVat?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺</span>}
            />
            <Column
              header=""
              bodyStyle={{ textAlign: 'right' }}
              body={(row) => (
                <Button
                  icon="pi pi-trash"
                  className="p-button-rounded p-button-text p-button-danger w-8 h-8 p-0"
                  onClick={() => handleDeleteSpecialPrice(row.productId)}
                />
              )}
            />
          </DataTable>
        )}
      </Dialog>

      <Dialog
        header="Özel Fiyat Tanımla"
        visible={showSpecialPriceDialog}
        style={{ width: '450px' }}
        modal
        onHide={() => setShowSpecialPriceDialog(false)}
        footer={
          <div className="flex justify-end gap-2">
            <Button label="İptal" icon="pi pi-times" className="p-button-text p-button-secondary" onClick={() => setShowSpecialPriceDialog(false)} />
            <Button label="Kaydet" icon="pi pi-check" className="p-button-success" onClick={handleSaveSpecialPrice} loading={submitting} />
          </div>
        }
      >
        <div className="flex flex-col gap-4 mt-2">
          <div className="flex flex-col gap-2">
            <label className="text-xs font-bold text-slate-600 dark:text-slate-400">Ürün Seçin</label>
            {!specialPriceForm.product ? (
              <div className="relative">
                <span className="p-input-icon-left w-full">
                  <i className="pi pi-search" />
                  <InputText
                    value={specialPriceForm.productQuery}
                    onChange={(e) => {
                      const val = e.target.value;
                      setSpecialPriceForm(prev => ({ ...prev, productQuery: val }));
                      searchSpecialPriceProduct(val);
                    }}
                    placeholder="Ürün adı veya barkod arayın..."
                    className="w-full"
                  />
                </span>
                {specialPriceForm.showProductsList && specialPriceForm.productsList.length > 0 && (
                  <div className="absolute left-0 right-0 z-50 mt-1 max-h-60 overflow-y-auto bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-xl rounded-xl">
                    {specialPriceForm.productsList.map((p) => (
                      <div
                        key={p.id || p.Id}
                        className="px-4 py-2 hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer flex justify-between items-center border-b border-slate-100 dark:border-slate-700 last:border-0"
                        onClick={() => selectSpecialPriceProduct(p)}
                      >
                        <span className="font-bold text-slate-800 dark:text-slate-200 text-sm">{p.name}</span>
                        <span className="text-xs text-slate-400">Fiyat: {p.salePrice?.toLocaleString()} ₺</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-between bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl px-4 py-3">
                <div className="flex flex-col">
                  <span className="font-black text-slate-800 dark:text-slate-200 text-sm">{specialPriceForm.product.name}</span>
                  <span className="text-xs text-slate-400">Normal Satış Fiyatı: {specialPriceForm.product.salePrice?.toLocaleString()} ₺</span>
                </div>
                <Button
                  icon="pi pi-refresh"
                  className="p-button-rounded p-button-text p-button-secondary w-8 h-8 p-0"
                  onClick={() => setSpecialPriceForm(prev => ({ ...prev, product: null, productQuery: "" }))}
                  tooltip="Ürünü Değiştir"
                />
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 mt-2">
            <Checkbox
              inputId="priceIsVatIncluded"
              checked={specialPriceForm.priceIsVatIncluded}
              onChange={(e) => {
                const checked = e.checked;
                const vatRate = specialPriceForm.product?.vatRate || 20;
                setSpecialPriceForm(prev => {
                  let incl = prev.priceInclVat;
                  let excl = prev.priceExclVat;
                  if (checked) {
                    incl = excl * (1 + vatRate / 100);
                  } else {
                    excl = incl / (1 + vatRate / 100);
                  }
                  return {
                    ...prev,
                    priceIsVatIncluded: checked,
                    priceInclVat: Number(incl.toFixed(4)),
                    priceExclVat: Number(excl.toFixed(4))
                  };
                });
              }}
            />
            <label htmlFor="priceIsVatIncluded" className="text-xs font-bold text-slate-600 dark:text-slate-400 cursor-pointer">Fiyat KDV Dahil</label>
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-xs font-bold text-slate-600 dark:text-slate-400">Birim Fiyat (Özel)</label>
            <InputNumber
              value={specialPriceForm.priceIsVatIncluded ? specialPriceForm.priceInclVat : specialPriceForm.priceExclVat}
              onValueChange={(e) => {
                const val = e.value || 0;
                const vatRate = specialPriceForm.product?.vatRate || 20;
                setSpecialPriceForm(prev => {
                  let incl = prev.priceInclVat;
                  let excl = prev.priceExclVat;
                  if (prev.priceIsVatIncluded) {
                    incl = val;
                    excl = val / (1 + vatRate / 100);
                  } else {
                    excl = val;
                    incl = val * (1 + vatRate / 100);
                  }
                  return {
                    ...prev,
                    priceInclVat: Number(incl.toFixed(4)),
                    priceExclVat: Number(excl.toFixed(4))
                  };
                });
              }}
              mode="currency"
              currency="TRY"
              locale="tr-TR"
              disabled={!specialPriceForm.product}
              autoFocus
            />
          </div>
        </div>
      </Dialog>

      <Dialog
        header="Açık Vadeli Faturaları Birleştir"
        visible={showMergeDialog}
        style={{ width: '700px' }}
        modal
        onHide={() => setShowMergeDialog(false)}
        footer={
          <div className="flex justify-content-end gap-2">
            <Button label="İptal" icon="pi pi-times" className="p-button-text p-button-secondary" onClick={() => setShowMergeDialog(false)} />
            <Button 
              label="Birleştir ve Yeni Fatura Oluştur" 
              icon="pi pi-check" 
              className="p-button-success" 
              onClick={handleMergeInvoicesSubmit} 
              loading={merging} 
              disabled={selectedMergeInvoices.length < 2} 
            />
          </div>
        }
      >
        <div className="mb-4">
          <p className="text-slate-600 dark:text-slate-400 text-sm">
            Müşteriye ait açık vadeli/açık hesap faturaları seçiniz. Birleştirilen faturalar <b>pasif</b> duruma getirilecek ve seçilen faturalardaki tüm ürünler yeni bir faturada birleştirilecektir.
          </p>
        </div>

        {/* Calendar for Due Date */}
        <div className="flex flex-col gap-2 mb-4 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-4">
          <label className="text-xs font-bold text-slate-700 dark:text-slate-300">
            Yeni Faturanın Vade Tarihi <span className="text-red-500 font-bold">*</span>
          </label>
          <Calendar 
            value={mergeDueDate} 
            onChange={(e) => setMergeDueDate(e.value)} 
            dateFormat="dd.mm.yy" 
            locale="tr" 
            showIcon 
            placeholder="Vade tarihi seçin..." 
            appendTo={document.body}
            className="p-inputtext-sm w-full"
          />
        </div>

        <div className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden max-h-60 overflow-y-auto">
          {(() => {
            const openCredInvoices = invoices.filter(inv => inv.receivableDueItemId && inv.remainingAmount > 0.01);
            return (
              <table className="w-full text-left border-collapse text-sm">
                <thead>
                  <tr className="bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-bold border-b border-slate-200 dark:border-slate-700">
                    <th className="p-3 w-16 text-center">
                      <Checkbox 
                        checked={openCredInvoices.length > 0 && selectedMergeInvoices.length === openCredInvoices.length} 
                        onChange={() => handleToggleAllMergeInvoices(openCredInvoices)} 
                      />
                    </th>
                    <th className="p-3">Fatura No</th>
                    <th className="p-3">Fatura Tarihi</th>
                    <th className="p-3 text-right">Toplam Tutar</th>
                    <th className="p-3 text-right">Ödenen</th>
                    <th className="p-3 text-right">Kalan Tutar</th>
                  </tr>
                </thead>
                <tbody>
                  {openCredInvoices.map((inv) => (
                    <tr 
                      key={inv.id} 
                      className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer"
                      onClick={() => handleToggleMergeInvoice(inv.id)}
                    >
                      <td className="p-3 text-center" onClick={(e) => e.stopPropagation()}>
                        <Checkbox 
                          checked={selectedMergeInvoices.includes(inv.id)} 
                          onChange={() => handleToggleMergeInvoice(inv.id)} 
                        />
                      </td>
                      <td className="p-3 font-semibold text-slate-800 dark:text-slate-200">{inv.saleNo || inv.id}</td>
                      <td className="p-3 text-slate-500">{inv.saleDate}</td>
                      <td className="p-3 text-right font-medium text-slate-800 dark:text-slate-200">
                        {inv.totalAmount?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                      </td>
                      <td className="p-3 text-right text-emerald-600 font-medium">
                        {inv.paidAmount?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                      </td>
                      <td className="p-3 text-right text-orange-600 font-bold">
                        {inv.remainingAmount?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                      </td>
                    </tr>
                  ))}
                  {openCredInvoices.length === 0 && (
                    <tr>
                      <td colSpan="6" className="p-4 text-center text-slate-500 italic">Açık vadeli fatura bulunamadı.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            );
          })()}
        </div>

        <div className="mt-3 flex justify-content-between text-xs text-slate-500">
          <span>Toplam {invoices.filter(inv => inv.receivableDueItemId && inv.remainingAmount > 0.01).length} açık fatura listelendi</span>
          <span>{selectedMergeInvoices.length} fatura seçildi</span>
        </div>
      </Dialog>
    </div>
  );
}
